/* S1 PDP configurator.
   Rules ported 1:1 from shopify-islands BoxerBriefProductSectionV2.tsx:
   - default tier from settings; `no-tier`/`nopacks` tags force qty 1
   - single mode / qty 1: allocation = { current: qty }
   - entering mixed mode resets allocation to { current: 1 }
   - tier downgrade trims allocation from the tail, preserving insertion order
   - displayed unit price = price * (1 - pct) where pct comes from the value
     tier matched against (cart subtotal + count * price), mirroring checkout
   - fit switching swaps this section in-page via the Section Rendering API,
     replacing the SPA's shopify_product_change + set_product/pushState bridge
   - ATC: per-colour variants matched by selected size; colours missing that
     size are skipped; same variant ids consolidated */

export class S1PdpStore {
  static SIZE_ALIAS = {
    XS: 'Extra Small', 'Extra Small': 'XS',
    S: 'Small', Small: 'S',
    M: 'Medium', Medium: 'M',
    L: 'Large', Large: 'L',
    XL: 'Extra Large', 'Extra Large': 'XL',
  };

  /* SPA sortProductsByStock target sizes for womens collections */
  static WOMENS_TARGET_SIZES = ['XS', 'S', 'M'];

  constructor(data) {
    this.data = data;
    this.products = { ...data.siblings, [data.currentHandle]: data.current };
    /* variant-colour families (socks): colour is a variant option on each
       product and mixed packs are built as SLOTS ({handle,size,colour})
       across the fit landing products, so those products join the map.
       Sibling-colour families never get fitProducts emitted, keeping their
       behaviour untouched. */
    /* absorbency products force the colour-matrix path even with one colour:
       the aggregate p.sizes map can't tell levels apart, so the allocation
       ATC path would add the wrong variant */
    this.slotMode = !!(data.current.colours
      && (Object.keys(data.current.colours).length > 1 || data.current.absorbencies));
    if (this.slotMode && data.fitProducts) {
      this.products = { ...this.products, ...data.fitProducts };
    }
    /* mix-collection families (womens): colour is a SIBLING PRODUCT and mixed
       packs are SLOTS ({handle, size}) drawn from every configured builder
       collection — all of those products join the map. mixProducts entries
       carry an image for the slot cards, so they win the merge. Mutually
       exclusive with slotMode (womens products have only a Size option). */
    this.collectionSlots = !this.slotMode && !!(data.mixProducts && Object.keys(data.mixProducts).length);
    if (this.collectionSlots) {
      this.products = { ...this.products, ...data.mixProducts };
    }
    /* fixed-content bundle (Shopify Bundles product, e.g. Welcome Pack):
       every product option is its own size dimension and the variant is the
       option-value tuple. Colours are fixed by the pack, so the colour/tier/
       builder machinery stays off; quantity is a free pack stepper. */
    this.bundleMode = !this.slotMode && !this.collectionSlots
      && !!(data.bundleOptions && data.bundleOptions.length);
    /* seeded from Liquid's cart.original_total_price so the very first
       render prices tiers against the real cart — no flash to empty-cart
       pricing on page load or fit switches; /cart.js refreshes keep it
       current afterwards */
    this.cartSubtotal = data.cartSubtotal || 0;
    /* add-on cents riding along with this add (the set card's socks): they
       count toward the value tier like anything else in the order, so the
       set's own price and pack prices move when the shopper toggles them */
    this.extraCents = 0;
    /* metals landing: the SPA stepper adjusts quantity freely even on
       no-tier products (buying N packs), so the pin-to-1 stands down */
    this.freeStepper = !!data.freeStepper;
    this.listeners = [];

    const packsDisabled = data.current.packsDisabled;
    const defaultTier = data.tiers[data.defaultTierIndex] || data.tiers[0];
    const tierQty = packsDisabled ? 1 : defaultTier.count;
    /* single-colour families (e.g. Cloud Mesh) have no colour grid to fill a
       mixed pack from — packs must allocate everything to the one product.
       Variant-colour families always have something to mix (colours/styles),
       but start in single mode like the SPA's bundleMode default. */
    this.singleColour = Object.keys(this.products).length <= 1 && !this.slotMode;
    /* womens SPA bundleMode defaults to 'single' too */
    const mode = this.singleColour || this.slotMode || this.collectionSlots ? 'single' : 'mixed';
    const sizes = data.current.sizes;
    const slotModeDefault = Object.keys(sizes).find((k) => sizes[k].available) || null;
    /* womens SPA getPreferredVariant walks S -> M -> XS -> L; the default
       list is the mens SPA's L -> XL -> M -> S */
    const preferred = data.sizePriority === 'womens'
      ? ['Small', 'S', 'Medium', 'M', 'Extra Small', 'XS', 'Large', 'L']
      : ['Large', 'L', 'Extra Large', 'XL', 'Medium', 'M', 'Small', 'S'];
    const defaultSize = this.slotMode
      ? slotModeDefault
      : preferred.find((k) => sizes[k] && sizes[k].available)
        || Object.keys(sizes).find((k) => sizes[k].available)
        || null;
    /* variant-colour default: first colour with the default size in stock,
       else first colour at all (SPA getVariantForSelection fallback) */
    let defaultColour = null;
    if (this.slotMode) {
      const colours = Object.keys(data.current.colours);
      defaultColour = colours.find((c) => {
        const cs = data.current.colours[c].sizes[defaultSize];
        return cs && cs.available;
      }) || colours[0] || null;
    }
    /* absorbency (period underwear): a third variant dimension between
       colour and size. Default = first level stocking the default size in
       the default colour, else first level with any stock, else the first
       level — mirroring the colour default above. */
    let defaultAbsorbency = null;
    const absList = data.current.absorbencies || null;
    if (absList && defaultColour) {
      const abs = data.current.colours[defaultColour].abs || {};
      const anySize = (level, wantSize) => {
        const lv = abs[level];
        if (!lv) return false;
        if (wantSize) return !!(lv.sizes[wantSize] && lv.sizes[wantSize].available);
        return Object.values(lv.sizes).some((v) => v.available);
      };
      defaultAbsorbency = absList.find((a) => anySize(a, defaultSize))
        || absList.find((a) => anySize(a, null))
        || absList[0];
    }

    this.state = {
      selectedHandle: data.currentHandle,
      size: defaultSize,
      colour: defaultColour,
      absorbency: defaultAbsorbency,
      tierQty,
      mode,
      view: 'all',
      allocation: { [data.currentHandle]: mode === 'mixed' ? 1 : tierQty },
      /* slotMode mixed packs: ordered slots of {handle, size, colour} */
      slots: [],
      /* bundleMode: one pending selection per option dimension */
      bundle: this.bundleMode ? data.bundleOptions.map(() => null) : null,
      loading: false,
    };

    /* bundle defaults ("Default size" on each bundle_sizes block): preselect
       a dimension only while the value is marginally orderable — sold-out
       values fall back to unselected. A complete default whose combination
       is out of stock also falls back entirely (no Out of Stock CTA on first
       paint); remote mode can't know the combination synchronously, so its
       CTA just waits for the option_values resolution like any selection. */
    if (this.bundleMode && data.bundleDefaults) {
      this.state.bundle = data.bundleOptions.map((opt, i) => {
        /* the default is a comma-separated candidate list ("Large, L") —
           pack products don't agree on value naming, and a shared template
           carries one default for all of them. First orderable match wins
           (bundleValueAvailable is false for names the product lacks). */
        const d = data.bundleDefaults[i];
        if (!d) return null;
        const pick = String(d)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .find((c) => this.bundleValueAvailable(i, c));
        return pick || null;
      });
      if (!data.bundleRemote && !this.state.bundle.some((v) => v == null)) {
        const v = this.bundleVariant();
        if (!v || !v.available) this.state.bundle = data.bundleOptions.map(() => null);
      }
    }
  }

  /* ---- fixed-content bundle helpers ---- */

  setBundleOption(idx, value) {
    if (!this.bundleMode || !this.data.bundleOptions[idx]) return;
    if (!this.data.bundleOptions[idx].values.some((v) => v.name === value)) return;
    this.state.bundle[idx] = value;
    this.emit('size');
  }

  bundleKey() {
    return this.state.bundle.join('|');
  }

  /* the selected option tuple's variant, null while incomplete/unknown.
     Local mode reads the island's lookup table; remote mode (>250 variants,
     where Liquid's table would lie) only trusts a server-resolved variant
     whose option tuple matches the CURRENT selection. */
  bundleVariant() {
    if (!this.bundleMode || this.state.bundle.some((v) => v == null)) return null;
    if (this.data.bundleRemote) {
      const r = this.bundleResolved;
      return r && r.key === this.bundleKey() ? r : null;
    }
    return this.data.bundleVariants[this.bundleKey()] || null;
  }

  /* marginal availability: a value is orderable if ANY variant carrying it
     is in stock (combination-level stock still gates the CTA). Remote mode
     reads the server-computed product_option_value availability, refreshed
     with every option_values fetch. */
  bundleValueAvailable(idx, value) {
    if (this.data.bundleRemote) {
      const v = this.data.bundleOptions[idx].values.find((x) => x.name === value);
      return !!(v && v.available);
    }
    const bv = this.data.bundleVariants || {};
    return Object.keys(bv).some((k) => k.split('|')[idx] === value && bv[k].available);
  }

  /* fold a fresh option_values section fetch into the store: per-value
     availability follows the new selection state; the resolved variant is
     kept with its tuple key so bundleVariant() can reject staleness */
  applyBundleState(fresh) {
    if (!this.bundleMode || !fresh || !fresh.bundleOptions) return;
    fresh.bundleOptions.forEach((opt, i) => {
      const mine = this.data.bundleOptions[i];
      if (!mine) return;
      opt.values.forEach((val) => {
        const target = mine.values.find((x) => x.name === val.name);
        if (target) target.available = val.available;
      });
    });
    if (fresh.bundleSelectedVariant) this.bundleResolved = fresh.bundleSelectedVariant;
    this.emit('size');
  }

  /* ---- variant-colour (slot mode) helpers ---- */

  colourVariant(handle, colour, size) {
    const p = this.products[handle];
    if (!p || !p.colours || !p.colours[colour]) return null;
    const key = this.resolveSizeKey(p, size);
    if (!key) return null;
    /* absorbency products resolve through the level matrix — the per-colour
       size map only aggregates availability across levels */
    if (p.absorbencies) {
      const lv = p.colours[colour].abs && p.colours[colour].abs[this.state.absorbency];
      return (lv && lv.sizes[key]) || null;
    }
    return p.colours[colour].sizes[key] || null;
  }

  setColour(colour) {
    if (!this.data.current.colours || !this.data.current.colours[colour]) return;
    this.state.colour = colour;
    this.emit('colour');
  }

  setAbsorbency(level) {
    const list = this.data.current.absorbencies;
    if (!list || !list.includes(level) || this.state.absorbency === level) return;
    this.state.absorbency = level;
    this.emit('absorbency');
  }

  addSlot(handle, colour) {
    if (this.state.slots.length >= this.state.tierQty) return;
    const p = this.products[handle];
    if (!p) return;
    /* mix-collection slot (womens): the tile IS the product; no colour key.
       SPA handleAddToMixedBundle seeds the slot with the global size only
       when this product stocks it, else the red "Select Size" state. */
    if (colour == null || colour === '') {
      if (!this.collectionSlots) return;
      this.slotAdded = true;
      const key = this.resolveSizeKey(p, this.state.size);
      this.state.slots.push({
        handle,
        colour: null,
        size: key && p.sizes[key].available ? this.state.size : '',
      });
      this.emit('slots');
      return;
    }
    if (!p.colours || !p.colours[colour]) return;
    this.slotAdded = true;
    /* seed the slot with the global size when that colour stocks it, else
       leave unset so the slot select shows the red "Select Size" state */
    const v = this.colourVariant(handle, colour, this.state.size);
    this.state.slots.push({
      handle,
      colour,
      size: v && v.available ? this.state.size : '',
    });
    this.emit('slots');
  }

  /* resolve a slot to its variant entry regardless of flavour: variant-colour
     slots go through the colour matrix, collection slots through the sibling
     product's size map (label aliases resolved — the SPA's exact-label match
     silently drops mismatched items at ATC; resolving is the safe port) */
  slotVariant(slot) {
    if (slot.colour != null) return this.colourVariant(slot.handle, slot.colour, slot.size);
    const p = this.products[slot.handle];
    if (!p) return null;
    const key = this.resolveSizeKey(p, slot.size);
    return (key && p.sizes[key]) || null;
  }

  removeSlot(index) {
    this.state.slots.splice(index, 1);
    this.emit('slots');
  }

  setSlotSize(index, size) {
    const slot = this.state.slots[index];
    if (!slot) return;
    slot.size = size;
    this.emit('slots');
  }

  /* NiteKnit bundle rows: a pack of N same-product slots, one per set.
     Mirrors the SPA's handleBundleSelect — every slot RE-seeds with the
     current colour/size on selection (Array(count).fill({color, size})); a
     seed whose variant is out of stock keeps size '' so the drawer shows
     the red Select Size state instead of silently pointing at OOS stock. */
  setBundleCount(count) {
    this.setTier(count);
    if (!this.slotMode) return;
    if (count <= 1) {
      this.state.mode = 'single';
      this.state.slots = [];
    } else {
      this.state.mode = 'mixed';
      const v = this.colourVariant(this.state.selectedHandle, this.state.colour, this.state.size);
      const size = v && v.available ? this.state.size : '';
      this.state.slots = Array.from({ length: count }, () => ({
        handle: this.state.selectedHandle,
        colour: this.state.colour,
        size,
      }));
    }
    this.emit('bundle');
  }

  subscribe(fn) { this.listeners.push(fn); }
  emit(type) { this.listeners.forEach((fn) => fn(type, this.state)); }

  get totalSelected() {
    if ((this.slotMode || this.collectionSlots) && this.effectiveMode === 'mixed') return this.state.slots.length;
    return Object.values(this.state.allocation).reduce((s, q) => s + q, 0);
  }

  get packsDisabled() { return this.data.current.packsDisabled; }
  /* limit-1 product tag: theme-side one-per-order, pins quantity to 1 regardless of layout */
  get limitOne() { return !!this.data.current.limitOne; }

  /* SPA keeps colorSelectionMode untouched when quantity hits 1; the single-
     colour lock is an effect of quantity, so dropping to 1 and back restores
     the user's mode choice */
  get effectiveMode() {
    if (this.singleColour) return 'single';
    /* absorbency slots aren't modelled yet (period packs are single-line
       quantity adds) — pin single until the slot shape carries a level */
    if (this.data.current.absorbencies) return 'single';
    /* SPA locks "Mix Colours & Styles" behind qty > 1 in both models */
    return this.state.tierQty === 1 ? 'single' : this.state.mode;
  }

  bumpTier(delta) {
    const next = Math.max(1, this.state.tierQty + delta);
    if (next !== this.state.tierQty) this.setTier(next);
  }

  /* tier card highlight is derived: highest configured tier <= quantity,
     mirroring BUNDLES.findIndex(b => quantity >= b.count) over the reversed
     list — including its -1 (no highlight) when qty sits below every tier */
  activeTierCount() {
    const counts = this.data.tiers.map((t) => t.count).sort((a, b) => b - a);
    const match = counts.find((c) => this.state.tierQty >= c);
    return match === undefined ? null : match;
  }

  /* SPA effect: an empty "Selected" filter falls back to the full grid —
     without this the filter button disables while the view stays filtered,
     stranding the user on an empty colour list */
  normalizeView() {
    if (this.state.view === 'selected' && this.totalSelected === 0) {
      this.state.view = 'all';
    }
  }

  resolveSizeKey(p, size) {
    if (!p || !size) return null;
    if (p.sizes[size]) return size;
    const alias = S1PdpStore.SIZE_ALIAS[size];
    if (alias && p.sizes[alias]) return alias;
    return null;
  }

  sizeAvailable(handle, size) {
    const p = this.products[handle];
    const key = this.resolveSizeKey(p, size);
    return !!(key && p.sizes[key].available);
  }

  setSize(size) {
    this.state.size = size;
    const alloc = this.state.allocation;
    for (const handle of Object.keys(alloc)) {
      if (!this.sizeAvailable(handle, size)) delete alloc[handle];
    }
    if (this.effectiveMode === 'single' && Object.keys(alloc).length === 0) {
      if (this.sizeAvailable(this.state.selectedHandle, size)) {
        alloc[this.state.selectedHandle] = this.state.tierQty;
      }
    }
    this.normalizeView();
    this.emit('size');
  }

  setTier(count) {
    /* womens SPA lets no-tier products take any quantity via the stepper
       (mixed stays locked because the builder never renders for them);
       other families keep the pin-to-1 */
    if (this.packsDisabled && !this.collectionSlots && !this.bundleMode && !this.freeStepper) count = 1;
    if (this.limitOne) count = 1;
    this.state.tierQty = count;
    if (this.slotMode || this.collectionSlots) {
      /* SPA truncates customBundleItems when quantity drops */
      if (this.state.slots.length > count) this.state.slots.length = count;
      /* womens SPA explicitly resets bundleMode to 'single' at quantity 1
         (handleBundleSelect + the stepper), so climbing back up stays single
         until the user re-toggles — unlike the mens grid which restores */
      if (this.collectionSlots && count <= 1) this.state.mode = 'single';
      if (this.effectiveMode === 'single') {
        this.state.allocation = { [this.state.selectedHandle]: count };
      }
      this.emit('tier');
      return;
    }
    if (this.effectiveMode === 'single') {
      this.state.allocation = { [this.state.selectedHandle]: count };
    } else {
      const alloc = this.state.allocation;
      const total = Object.values(alloc).reduce((s, q) => s + q, 0);
      if (total === 0) {
        this.state.allocation = { [this.state.selectedHandle]: 1 };
      } else if (total > count) {
        const trimmed = {};
        let remaining = count;
        for (const [handle, q] of Object.entries(alloc)) {
          const take = Math.min(q, remaining);
          if (take > 0) { trimmed[handle] = take; remaining -= take; }
        }
        this.state.allocation = trimmed;
      }
    }
    this.normalizeView();
    this.emit('tier');
  }

  setMode(mode) {
    if (this.singleColour || this.packsDisabled || this.state.tierQty === 1) mode = 'single';
    this.state.mode = mode;
    if (this.slotMode || this.collectionSlots) {
      /* SPA seeds the builder with one slot mirroring the current selection
         the first time mixed mode opens */
      if (mode === 'mixed' && this.state.slots.length === 0) {
        /* seed only carries the size when the current colour stocks it,
           matching addSlot — otherwise the slot opens in the red
           "Select Size" state instead of silently pointing at an OOS variant */
        if (this.collectionSlots) {
          const key = this.resolveSizeKey(this.data.current, this.state.size);
          this.state.slots.push({
            handle: this.state.selectedHandle,
            colour: null,
            size: key && this.data.current.sizes[key].available ? this.state.size : '',
          });
        } else {
          const seedV = this.colourVariant(this.state.selectedHandle, this.state.colour, this.state.size);
          this.state.slots.push({
            handle: this.state.selectedHandle,
            colour: this.state.colour,
            size: seedV && seedV.available ? this.state.size : '',
          });
        }
      }
      /* collection slots ride the sibling allocation path in single mode */
      if (this.collectionSlots && mode === 'single') {
        this.state.allocation = { [this.state.selectedHandle]: this.state.tierQty };
      }
      this.emit('mode');
      return;
    }
    if (mode === 'single') {
      this.state.allocation = { [this.state.selectedHandle]: this.state.tierQty };
    } else {
      this.state.allocation = { [this.state.selectedHandle]: 1 };
    }
    this.emit('mode');
  }

  setView(view) {
    this.state.view = view;
    this.emit('view');
  }

  snapshot() {
    return {
      size: this.state.size,
      tierQty: this.state.tierQty,
      mode: this.state.mode,
      view: this.state.view,
      allocation: this.effectiveMode === 'mixed' && !this.slotMode && !this.collectionSlots ? { ...this.state.allocation } : null,
      colour: this.slotMode ? this.state.colour : null,
      absorbency: this.state.absorbency || null,
      slots: this.slotMode || this.collectionSlots ? this.state.slots.map((s) => ({ ...s })) : null,
      /* bundle selections keyed by option NAME (pack products disagree on
         option order); only honoured on restore when the section opts in
         via data-bundle-keep-sizes, so legacy bundles still reset */
      bundle: this.bundleMode && Array.isArray(this.state.bundle)
        ? this.state.bundle.reduce((acc, v, i) => {
          if (v != null && this.data.bundleOptions[i]) acc[this.data.bundleOptions[i].name] = v;
          return acc;
        }, {})
        : null,
    };
  }

  /* opt-in (section setting "Keep bundle sizes on pack switch"): re-apply a
     migrated bundle selection to the new product. Match by option name,
     then by the sock/non-sock role (the same rule the Liquid blocks use),
     and only while the value is still marginally orderable here. A complete
     tuple whose combination is out of stock falls back to the defaults the
     constructor already applied, mirroring the bundleDefaults guard. */
  restoreBundle(byName) {
    if (!this.bundleMode || !byName || !this.data.bundleOptions.length) return;
    const isSock = (n) => String(n || '').toLowerCase().includes('sock');
    const prev = this.state.bundle.slice();
    let touched = false;
    this.data.bundleOptions.forEach((opt, i) => {
      let v = byName[opt.name];
      if (v == null) {
        const roleKey = Object.keys(byName).find((k) => isSock(k) === isSock(opt.name));
        if (roleKey) v = byName[roleKey];
      }
      if (v != null && this.bundleValueAvailable(i, v)) {
        this.state.bundle[i] = v;
        touched = true;
      }
    });
    if (!touched) return;
    if (!this.data.bundleRemote && !this.state.bundle.some((v) => v == null)) {
      const variant = this.bundleVariant();
      if (!variant || !variant.available) { this.state.bundle = prev; return; }
    }
    this.emit('size');
  }

  restore(snap) {
    if (!snap) return;
    if (snap.tierQty) this.setTier(snap.tierQty);
    if (snap.mode === 'mixed') {
      this.setMode('mixed');
      if (snap.allocation) {
        const alloc = {};
        for (const [handle, q] of Object.entries(snap.allocation)) {
          if (this.products[handle]) alloc[handle] = q;
        }
        if (Object.keys(alloc).length) this.state.allocation = alloc;
      }
    } else if (snap.mode === 'single') {
      this.setMode('single');
    }
    if (snap.view) this.state.view = snap.view;
    if (snap.size) {
      const key = this.resolveSizeKey(this.data.current, snap.size);
      /* womens (collectionSlots): dimmed size-unavailable colour tiles stay
         CLICKABLE, so restoring an out-of-stock size here would dead-end the
         CTA on "Out of Stock". The SPA's handleProductChange only keeps the
         size when the new product stocks it, else falls back to the womens
         preferred order — which the constructor already applied. Other
         families keep the old unconditional restore (their blocked tiles
         hide, so this path never fires with an OOS size). */
      if (key && (!this.collectionSlots || this.data.current.sizes[key].available)) this.setSize(key);
    }
    if (this.slotMode || this.collectionSlots) {
      if (this.slotMode && snap.colour && this.data.current.colours[snap.colour]) {
        this.state.colour = snap.colour;
      }
      /* absorbency rides fit switches only when the new style offers the
         same level (thong is Light-only, briefs are Moderate/Heavy) — the
         constructor's first-available default already covers the miss */
      const absList = this.data.current.absorbencies;
      if (absList && snap.absorbency && absList.includes(snap.absorbency)) {
        this.state.absorbency = snap.absorbency;
      }
      if (Array.isArray(snap.slots)) {
        /* keep slots whose product (+colour, variant families) still resolve
           after the switch; sizes that no longer resolve to an available
           variant drop back to the red "Select Size" state rather than
           riding along stale */
        this.state.slots = snap.slots.filter((slot) => {
          const p = this.products[slot.handle];
          if (!p) return false;
          if (slot.colour == null) return this.collectionSlots;
          return p.colours && p.colours[slot.colour];
        }).map((slot) => {
          if (!slot.size) return { ...slot };
          const v = this.slotVariant(slot);
          return { ...slot, size: v && v.available ? slot.size : '' };
        }).slice(0, this.state.tierQty);
      }
    }
  }

  bumpColour(handle, delta) {
    const alloc = this.state.allocation;
    const current = alloc[handle] || 0;
    if (delta > 0 && this.totalSelected >= this.state.tierQty) return;
    if (delta > 0 && this.state.size && !this.sizeAvailable(handle, this.state.size)) return;
    const next = Math.max(0, current + delta);
    if (next === 0) delete alloc[handle];
    else alloc[handle] = next;
    this.normalizeView();
    this.emit('colour');
  }

  /* cents the value-tier maths projects on top of the pack itself. The
     "Pack button pricing" setting flips this: Calculated (default) uses the
     live cart subtotal plus any riding add-ons, Static ignores the cart so
     every pack button prices as if the cart were empty and never moves as
     the cart fills. */
  projectionCents() {
    return this.data.staticTierPricing ? 0 : this.cartSubtotal + this.extraCents;
  }

  valueTierPct(projectedCents) {
    let pct = 0;
    for (const t of this.data.valueTiers) {
      if (projectedCents >= t.threshold && t.pct > pct) pct = t.pct;
    }
    return pct;
  }

  /* Socks v2 readout nudge: the cheapest value tier that would BEAT the
     discount `count` units already earn (cart-projected, like
     unitPriceCents). null when no higher tier exists or pricing is not
     value-tier based (dollar / fixed modes have no cart threshold). */
  nextValueTier(count) {
    if (this.data.dollarPricing || this.data.fixedPricing) return null;
    const projected = this.projectionCents() + count * this.currentPricing().price;
    const curPct = this.valueTierPct(projected);
    let best = null;
    for (const t of this.data.valueTiers || []) {
      if (t.threshold > projected && t.pct > curPct && (!best || t.threshold < best.threshold)) best = t;
    }
    if (!best) return null;
    return { gapCents: best.threshold - projected, pct: best.pct, threshold: best.threshold };
  }

  /* XCup fixed pricing (SPA BUNDLES): each tier carries its own fixed
     percentage, independent of the cart subtotal. null = fixed mode off;
     an unmatched count shows full price like the SPA's implicit 0. */
  fixedPctFor(count) {
    if (!this.data.fixedPricing) return null;
    const tier = (this.data.tiers || []).find((t) => t.count === count);
    return tier ? tier.pct || 0 : 0;
  }

  /* Legacy pack model (Kids): quantity thresholds take a flat amount off
     EVERY unit (custom.js getTieredPricing — AU 4+/$2, 7+/$4, 15+/$10).
     The tier with the LARGEST count at or below the requested quantity
     wins, so intermediate quantities (5, 9) keep the tier below them.
     null = dollar mode off. */
  dollarOffFor(count) {
    if (!this.data.dollarPricing) return null;
    let best = null;
    for (const t of this.data.tiers || []) {
      if (count >= t.count && (!best || t.count > best.count)) best = t;
    }
    /* a negative configured off must never raise the price above base */
    return best ? Math.max(0, best.off || 0) : 0;
  }

  /* selected-variant pricing (womens SPA: title %OFF badge, strike, red
     price and tier maths all follow selectedVariant). Sizes without per-size
     cents (every other family) fall back to the product-level figures, so
     behaviour is unchanged there. */
  currentPricing() {
    const cur = this.data.current;
    const key = this.resolveSizeKey(cur, this.state.size);
    const sv = key ? cur.sizes[key] : null;
    return {
      price: sv && sv.pr != null ? sv.pr : cur.price,
      compareAt: sv && sv.cap != null ? sv.cap : (cur.compareAt || 0),
    };
  }

  unitPriceCents(count) {
    const price = this.currentPricing().price;
    const dollarOff = this.dollarOffFor(count);
    if (dollarOff !== null) return Math.max(0, price - dollarOff);
    const fixedPct = this.fixedPctFor(count);
    if (fixedPct !== null) return Math.round(price * (1 - fixedPct / 100));
    const pct = this.valueTierPct(this.projectionCents() + count * price);
    return Math.round(price * (1 - pct / 100));
  }

  /* SPA button states only: Adding... (spinner) / Out of Stock / Add to
     Cart. An incomplete pack disables the button but keeps the default
     label — no price, no "Select N more". */
  get ctaState() {
    const { size, tierQty } = this.state;
    if (this.state.loading) return { disabled: true, label: 'Adding...', loading: true };
    /* limit-1 tag: one unit per order. Once it is in the cart the CTA turns
       into a "view cart" action instead of adding a second unit that the
       drawer would only knock back to 1 */
    if (this.limitOne && this.inCartQty > 0) return { disabled: false, label: 'Added · View cart', inCart: true };
    if (this.bundleMode) {
      /* disabled (no label) until every size dimension is picked; a picked
         combination whose variant is sold out reads Out of Stock */
      if (this.state.bundle.some((v) => v == null)) return { disabled: true, label: null };
      const v = this.bundleVariant();
      /* remote mode: a complete selection whose server resolution hasn't
         landed yet (fetch in flight / failed) stays quietly disabled — a
         sold-out verdict needs an actual answer */
      if (!v) {
        if (this.data.bundleRemote) return { disabled: true, label: null };
        return { disabled: true, label: 'Out of Stock' };
      }
      if (!v.available) return { disabled: true, label: 'Out of Stock' };
      return { disabled: false, label: null };
    }
    if (this.collectionSlots && this.effectiveMode === 'mixed') {
      /* womens SPA disables the mixed CTA when the globally selected variant
         is sold out (its `!selectedVariant.availableForSale` term applies in
         both modes), even with every slot resolvable */
      if (!size || !this.sizeAvailable(this.state.selectedHandle, size)) {
        return { disabled: true, label: 'Out of Stock' };
      }
      /* disabled until every slot exists and carries a size; the theme
         additionally requires each slot to resolve to in-stock */
      const incomplete = this.state.slots.length < tierQty
        || this.state.slots.some((slot) => {
          if (!slot.size) return true;
          const v = this.slotVariant(slot);
          return !(v && v.available);
        });
      return { disabled: incomplete, label: null };
    }
    if (this.slotMode) {
      if (this.effectiveMode === 'mixed') {
        /* SPA: disabled until every slot exists and has a size; the theme
           additionally requires each slot to resolve to an in-stock variant
           so buildItems can never silently under-fill the pack */
        const incomplete = this.state.slots.length < tierQty
          || this.state.slots.some((slot) => {
            if (!slot.size) return true;
            const v = this.colourVariant(slot.handle, slot.colour, slot.size);
            return !(v && v.available);
          });
        if (incomplete) return { disabled: true, label: null };
        return { disabled: false, label: null };
      }
      const v = this.colourVariant(this.state.selectedHandle, this.state.colour, size);
      if (!v || !v.available) return { disabled: true, label: 'Out of Stock' };
      return { disabled: false, label: null };
    }
    if (!size || !this.sizeAvailable(this.state.selectedHandle, size)) {
      return { disabled: true, label: 'Out of Stock' };
    }
    if (tierQty > 1 && this.totalSelected < tierQty) {
      return { disabled: true, label: null };
    }
    return { disabled: false, label: null };
  }

  buildItems() {
    const { size } = this.state;
    const items = [];
    if (this.bundleMode) {
      /* one line item: the bundle variant x pack quantity (checkout expands
         the components itself) */
      const v = this.bundleVariant();
      return v && v.available ? [{ id: v.id, quantity: this.state.tierQty }] : [];
    }
    if (this.collectionSlots && this.effectiveMode === 'mixed') {
      /* womens SPA mixed ATC: one unit per slot, resolved against the slot
         product's own size map, then consolidated by variant id */
      for (const slot of this.state.slots) {
        if (!slot.size) continue;
        const v = this.slotVariant(slot);
        if (v && v.available) items.push({ id: v.id, quantity: 1 });
      }
      if (this.upsellItems) items.push(...this.upsellItems());
      const merged = [];
      for (const item of items) {
        const existing = merged.find((i) => i.id === item.id);
        if (existing) existing.quantity += item.quantity;
        else merged.push({ ...item });
      }
      return merged;
    }
    if (this.slotMode) {
      if (this.effectiveMode === 'mixed') {
        for (const slot of this.state.slots) {
          if (!slot.size) continue;
          const v = this.colourVariant(slot.handle, slot.colour, slot.size);
          if (v && v.available) items.push({ id: v.id, quantity: 1 });
        }
      } else {
        const v = this.colourVariant(this.state.selectedHandle, this.state.colour, size);
        if (v && v.available) items.push({ id: v.id, quantity: this.state.tierQty });
      }
      if (this.upsellItems) items.push(...this.upsellItems());
      const merged = [];
      for (const item of items) {
        const existing = merged.find((i) => i.id === item.id);
        if (existing) existing.quantity += item.quantity;
        else merged.push({ ...item });
      }
      return merged;
    }
    for (const [handle, q] of Object.entries(this.state.allocation)) {
      if (q <= 0) continue;
      const p = this.products[handle];
      const key = this.resolveSizeKey(p, size);
      const v = key && p.sizes[key];
      if (v && v.available) items.push({ id: v.id, quantity: q });
    }
    /* sock upsell rides along with the main ATC, consolidated below like the
       SPA's variantsToAdd merge */
    if (this.upsellItems) items.push(...this.upsellItems());
    const consolidated = [];
    for (const item of items) {
      const existing = consolidated.find((i) => i.id === item.id);
      if (existing) existing.quantity += item.quantity;
      else consolidated.push({ ...item });
    }
    return consolidated;
  }

  async addToCart() {
    const items = this.buildItems();
    if (!items.length) return;
    this.state.loading = true;
    this.state.cartError = null;
    this.emit('loading');
    const directAdd = async () => {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        /* surface Shopify's 422 reason (sold out mid-session, purchase limit)
           instead of failing silently */
        let description = '';
        try {
          const body = await res.json();
          description = body.description || body.message || '';
        } catch (parseErr) { /* non-JSON error body */ }
        const err = new Error(`cart/add ${res.status}`);
        err.userMessage = description;
        throw err;
      }
      return res.json();
    };
    try {
      let usedDirect = false;
      if (window._s1 && window._s1.addToCart && window._s1.refreshCart) {
        /* mirror _s1.addToCartAndRefreshCart but validate between the steps:
           _s1.addToCart never checks response.ok, so a 422 (sold out
           mid-session, purchase limit) resolves with Shopify's error body and
           the pass-through used to treat it as success — no error shown,
           Klaviyo fired, refreshCart broadcast the error object as a
           variant:add. Shopify error bodies carry a numeric `status`; real
           add payloads never do. */
        let body = null;
        try {
          body = await window._s1.addToCart(items);
        } catch (err) {
          console.error('s1-pdp: _s1 add failed, falling back', err);
          await directAdd();
          usedDirect = true;
        }
        if (!usedDirect) {
          if (body && typeof body.status === 'number' && body.status >= 400) {
            const err = new Error(`cart/add ${body.status}`);
            err.userMessage = body.description || body.message || '';
            throw err;
          }
          /* the add landed — a refresh hiccup must NOT re-add (double
             quantity) or read as an add failure; degrade to the direct
             path's own cart:change broadcast instead */
          try {
            await window._s1.refreshCart(body);
          } catch (err) {
            console.error('s1-pdp: cart refresh failed after add', err);
            usedDirect = true;
          }
        }
      } else {
        await directAdd();
        usedDirect = true;
      }
      await this.refreshSubtotal();
      /* the direct path bypasses the theme's cart plumbing, so broadcast the
         change ourselves — a section swapped in mid-add hears this and
         refreshes its own subtotal/tier pricing */
      if (usedDirect) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change'));
      }
      /* analytics ride-along (Klaviyo Added to Cart, wired by the element) —
         never allowed to fail the purchase path */
      if (this.onAddSuccess) {
        try { this.onAddSuccess(); } catch (e) { console.error('s1-pdp: add analytics', e); }
      }
    } catch (err) {
      console.error('s1-pdp: add to cart failed', err, items);
      this.state.cartError = err.userMessage
        || 'Could not add to cart. Please try again.';
      /* stock may have changed under us — re-sync so the retry renders
         against fresh cart state */
      this.refreshSubtotal();
    } finally {
      this.state.loading = false;
      this.emit('loading');
    }
  }

  async refreshSubtotal() {
    /* concurrent refreshes (post-ATC + cart:change) can return out of order;
       only the latest request may write the subtotal */
    const seq = (this._subtotalSeq = (this._subtotalSeq || 0) + 1);
    try {
      const cart = await (await fetch('/cart.js')).json();
      if (seq !== this._subtotalSeq) return;
      this.cartSubtotal = cart.original_total_price || cart.total_price || 0;
      /* limit-1: units of this product already in the cart (drives the
         "In your cart" CTA state) */
      const handle = this.data.current && this.data.current.handle;
      this.inCartQty = (cart.items || []).filter((i) => i.handle === handle).reduce((n, i) => n + i.quantity, 0);
      this.emit('cart');
    } catch (e) {
      /* price fallback: undiscounted Liquid render stays visible */
    }
  }
}

/* Symbol for the market's active currency. AUD/USD keep the bare '$' the
   store has always shown; every other currency (GBP, EUR, INR, AED, ...) is
   asked of Intl so new markets need no code change. Cached per currency. */
const CURRENCY_SYMBOLS = { AUD: '$', USD: '$', GBP: '£', EUR: '€' };

const currencySymbol = () => {
  const active = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD';
  if (!CURRENCY_SYMBOLS[active]) {
    try {
      const part = new Intl.NumberFormat('en', { style: 'currency', currency: active })
        .formatToParts(0).find((p) => p.type === 'currency');
      CURRENCY_SYMBOLS[active] = part && /^[^A-Z]+$/.test(part.value) ? part.value : active + ' ';
    } catch (e) {
      CURRENCY_SYMBOLS[active] = active + ' ';
    }
  }
  return CURRENCY_SYMBOLS[active];
};

/* ---- size chart helpers ----
   Rows come from the `size_chart` metaobject (scripts/size-charts/size-charts.json
   is the seed). Ranges are [min, max] in cm; inches are derived here so the two
   units can never drift. Pure functions — covered by tests/s1-size-chart.test.mjs. */
const s1CmToIn = (cm) => Math.round((cm / 2.54) * 2) / 2;

const s1RangeText = (pair, unit, plus) => {
  if (!Array.isArray(pair)) return 'N/A';
  const f = unit === 'in' ? s1CmToIn : (v) => v;
  return `${f(pair[0])}–${f(pair[1])}${plus ? '+' : ''}`;
};

/* Index of the row whose `key` range holds `v` (cm). Rows share edges
   (73–80, 80–88): an edge value goes to the LARGER size, matching the
   "between sizes, size up" copy. A gap between rows rounds up to the next
   size; above the top row → top row; below the first → first. -1 when the
   table has no such ranges or v is not a positive number. */
const s1RowIndexFor = (rows, key, v) => {
  const ranged = rows.map((r, i) => ({ r, i })).filter((x) => Array.isArray(x.r[key]) && x.r[key].length === 2);
  if (!ranged.length || !(v > 0)) return -1;
  for (let k = 0; k < ranged.length; k++) {
    const [min, max] = ranged[k].r[key];
    const last = k === ranged.length - 1;
    if (v >= min && (v < max || (last && v <= max))) return ranged[k].i;
  }
  if (v > ranged[ranged.length - 1].r[key][1]) return ranged[ranged.length - 1].i;
  if (v < ranged[0].r[key][0]) return ranged[0].i;
  const next = ranged.find((x) => x.r[key][0] > v);
  return next ? next.i : ranged[ranged.length - 1].i;
};

/* calculator "waist": waist only. "waist_hip": both measured, the larger
   size wins (comfort rule). Returns the row's size label or null. */
const s1SizeFromMeasurements = (rows, calculator, { waistCm, hipCm }) => {
  const wi = waistCm ? s1RowIndexFor(rows, 'waist_cm', waistCm) : -1;
  const hi = calculator === 'waist_hip' && hipCm ? s1RowIndexFor(rows, 'hip_cm', hipCm) : -1;
  const i = Math.max(wi, hi);
  return i >= 0 ? rows[i].size : null;
};

/* calculator "bra": typed size (12C, 34 D, 36dd) looked up in every
   region's list. null when unrecognised — the modal says so instead of
   guessing M like the SPA did. */
const s1BraSizeFor = (rows, raw) => {
  const clean = String(raw || '').toUpperCase().replace(/\s/g, '');
  if (!clean) return null;
  const hit = rows.find((r) => ['au', 'uk', 'us'].some((k) => Array.isArray(r[k]) && r[k].includes(clean)));
  return hit ? hit.size : null;
};

/* Hide chart rows for sizes the product doesn't sell (Boyleg stops at XL,
   Boxer Brief Fly starts at M). `values` are the product's Size option
   values, matched directly or via S1PdpStore.SIZE_ALIAS. If nothing matches
   (junior "Ages 2-3" vs "2-3 years", bundles) the table is left whole. */
const s1FilterRowsToProduct = (rows, values) => {
  if (!Array.isArray(values) || !values.length) return rows;
  const has = (size) => values.includes(size) || values.includes(S1PdpStore.SIZE_ALIAS[size]);
  const kept = rows.filter((r) => has(r.size));
  return kept.length ? kept : rows;
};

const moneyFormat = (cents) => {
  const value = cents / 100;
  return currencySymbol() + (value % 1 === 0 ? String(value) : value.toFixed(2));
};

/* XCup SPA: bundle prices always render two decimals (toFixed(2)) */
const moneyFormatCents = (cents) => currencySymbol() + (cents / 100).toFixed(2);

/* ---- shared overlay plumbing ----
   Body scroll locking is ref-counted so stacked overlays (drawer + fullscreen
   on desktop) don't unlock early; each overlay must only call lockBody on
   real open/close transitions. */
let bodyLockCount = 0;
/* SPA getSizeLabel: collapse long size names in compact UIs */
const SHORT_SIZE = { 'Extra Small': 'XS', Small: 'S', Medium: 'M', Large: 'L', 'Extra Large': 'XL' };

const lockBody = (on) => {
  bodyLockCount = Math.max(0, bodyLockCount + (on ? 1 : -1));
  document.body.style.overflow = bodyLockCount > 0 ? 'hidden' : '';
};

/* minimal dialog a11y: focus goes in on open, Escape closes, Tab wraps.
   Overlays can stack (drawer + fullscreen gallery on desktop), so handlers
   register on a stack and only the topmost dialog answers Escape/Tab —
   otherwise one keypress closes every layer and the traps fight over focus. */
const dialogStack = [];
const openDialog = (root, onClose) => {
  const prevFocus = document.activeElement;
  const focusables = () => root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
  const first = focusables()[0];
  if (first) first.focus();
  const token = {};
  dialogStack.push(token);
  const onKeydown = (e) => {
    if (dialogStack[dialogStack.length - 1] !== token) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const f = focusables();
    if (!f.length) return;
    const firstEl = f[0];
    const lastEl = f[f.length - 1];
    if (e.shiftKey && document.activeElement === firstEl) {
      lastEl.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === lastEl) {
      firstEl.focus();
      e.preventDefault();
    }
  };
  document.addEventListener('keydown', onKeydown);
  return () => {
    document.removeEventListener('keydown', onKeydown);
    const idx = dialogStack.indexOf(token);
    if (idx !== -1) dialogStack.splice(idx, 1);
    if (prevFocus && prevFocus.isConnected) prevFocus.focus();
  };
};

/* ---- Find-my-fit size modal ----
   Markup: snippets/s1-pdp-size-modal.liquid. Data: the `size_chart`
   metaobject entry the snippet resolved, inlined as JSON. Chart columns and
   calculator inputs follow the entry's chart_type / calculator fields — no
   product-handle sniffing here. */
class S1SizeModal {
  constructor(root, onSelectSize) {
    this.root = root;
    this.onSelectSize = onSelectSize;
    this.chartType = root.dataset.s1ChartType || 'waist_hip';
    this.calculator = root.dataset.s1Calculator || 'waist';
    /* metaobject hide_columns: chart columns suppressed even when rows carry
       the data (mens hides hip like the old modal; girls shows cm only) */
    this.hideCols = new Set((root.dataset.s1HideColumns || '').split(',').map((c) => c.trim()).filter(Boolean));
    this.isBra = this.calculator === 'bra';
    let rows = [];
    let sizes = [];
    try {
      const rowsEl = root.querySelector('[data-s1-size-rows]');
      rows = rowsEl ? JSON.parse(rowsEl.textContent) : [];
      const sizesEl = root.querySelector('[data-s1-product-sizes]');
      sizes = sizesEl ? JSON.parse(sizesEl.textContent) : [];
    } catch (e) {
      rows = [];
    }
    this.allRows = Array.isArray(rows) ? rows : [];
    this.rows = s1FilterRowsToProduct(this.allRows, sizes);
    this.unit = 'cm';
    this.recommendation = null;

    this.waistInput = root.querySelector('[data-s1-modal-waist]');
    this.hipInput = root.querySelector('[data-s1-modal-hip]');
    this.braInput = root.querySelector('[data-s1-modal-bra]');
    this.calcBtn = root.querySelector('[data-s1-modal-calculate]');
    this.noMatch = root.querySelector('[data-s1-modal-nomatch]');
    this.buildChart();
    this.bind();
  }

  buildChart() {
    const head = this.root.querySelector('[data-s1-modal-chart-head]');
    const body = this.root.querySelector('[data-s1-modal-chart-body]');
    if (!head || !body) return;
    const th = (cols) => cols.map((c) => `<th>${c}</th>`).join('');
    const sizeTd = (d) => `<td class="s1-pdp__size-modal-td-size">${d.size}</td>`;
    const rows = this.rows;

    if (this.chartType === 'shoe') {
      head.innerHTML = th(['Size', 'EU', "Men's US", "Women's US", 'UK']);
      body.innerHTML = rows.map((d) => `<tr>${sizeTd(d)}<td>${d.eu || ''}</td><td>${d.mens_us || ''}</td><td>${d.womens_us || ''}</td><td>${d.uk || ''}</td></tr>`).join('');
      return;
    }
    if (this.chartType === 'bra') {
      const list = (v) => (Array.isArray(v) ? v.join(', ') : '');
      head.innerHTML = th(['Size', 'AU/NZ', 'UK', 'US']);
      body.innerHTML = rows.map((d) => `<tr>${sizeTd(d)}<td>${list(d.au)}</td><td>${list(d.uk)}</td><td>${list(d.us)}</td></tr>`).join('');
      return;
    }

    /* waist_hip / waist_only / ages: columns appear when any row carries
       the field, so one renderer serves mens (waist + hip), sleepwear
       (waist only), junior (+ height, old sizing) and girls (no hip). */
    const hide = this.hideCols;
    const any = (k) => !hide.has(k) && rows.some((d) => d[k] !== undefined && d[k] !== null && d[k] !== '');
    const cols = [this.chartType === 'ages' ? 'Ages' : 'Size'];
    const cells = [sizeTd];
    [['au_size', 'AU Size'], ['uk_size', 'UK Size'], ['us_size', 'US Size']].forEach(([k, label]) => {
      if (any(k)) { cols.push(label); cells.push((d) => `<td>${d[k] || ''}</td>`); }
    });
    if (any('waist_cm')) {
      cols.push('Waist (CM)'); cells.push((d) => `<td>${s1RangeText(d.waist_cm, 'cm', d.plus)}</td>`);
      if (!hide.has('waist_in')) { cols.push('Waist (IN)'); cells.push((d) => `<td>${s1RangeText(d.waist_cm, 'in', d.plus)}</td>`); }
    }
    if (any('hip_cm') && !hide.has('hip')) {
      cols.push('Hip (CM)'); cells.push((d) => `<td>${s1RangeText(d.hip_cm, 'cm', d.plus)}</td>`);
      if (!hide.has('hip_in')) { cols.push('Hip (IN)'); cells.push((d) => `<td>${s1RangeText(d.hip_cm, 'in', d.plus)}</td>`); }
    }
    if (any('height_cm') && !hide.has('height')) {
      cols.push('Height (CM)');
      cells.push((d) => `<td>${s1RangeText(d.height_cm, 'cm')}</td>`);
    }
    if (any('old_sizing')) {
      cols.push('Old Sizing');
      cells.push((d) => `<td>${d.old_sizing || 'N/A'}</td>`);
    }
    head.innerHTML = th(cols);
    body.innerHTML = rows.map((d) => `<tr>${cells.map((fn) => fn(d)).join('')}</tr>`).join('');
  }

  bind() {
    this.root.addEventListener('click', (e) => {
      if (e.target.closest('[data-s1-size-modal-close]')) return this.close();
      const viewBtn = e.target.closest('[data-s1-modal-view]');
      if (viewBtn) return this.setView(viewBtn.dataset.s1ModalView);
      const unitBtn = e.target.closest('[data-s1-modal-unit]');
      if (unitBtn) return this.setUnit(unitBtn.dataset.s1ModalUnit);
      if (e.target.closest('[data-s1-modal-calculate]')) return this.calculate();
      if (e.target.closest('[data-s1-modal-reset]')) return this.reset();
      if (e.target.closest('[data-s1-modal-select]')) {
        if (this.recommendation && this.onSelectSize) this.onSelectSize(this.recommendation);
        return this.close();
      }
    });
    const updateCalcEnabled = () => {
      if (!this.calcBtn) return;
      if (this.noMatch) this.noMatch.hidden = true;
      if (this.isBra) {
        this.calcBtn.disabled = !this.braInput || this.braInput.value.trim() === '';
        return;
      }
      const waistEmpty = !this.waistInput || this.waistInput.value.trim() === '';
      const hipEmpty = !this.hipInput || this.hipInput.value.trim() === '';
      this.calcBtn.disabled = waistEmpty && hipEmpty;
    };
    if (this.waistInput) this.waistInput.addEventListener('input', updateCalcEnabled);
    if (this.hipInput) this.hipInput.addEventListener('input', updateCalcEnabled);
    if (this.braInput) this.braInput.addEventListener('input', updateCalcEnabled);
  }

  open() {
    if (!this.root.hidden) return;
    this.root.hidden = false;
    lockBody(true);
    this._closeDialog = openDialog(this.root, () => this.close());
  }

  close() {
    if (this.root.hidden) return;
    this.root.hidden = true;
    lockBody(false);
    if (this._closeDialog) {
      this._closeDialog();
      this._closeDialog = null;
    }
  }

  setView(view) {
    const calc = this.root.querySelector('[data-s1-modal-calculator]');
    const chart = this.root.querySelector('[data-s1-modal-chart]');
    if (calc) calc.hidden = view !== 'calculator';
    if (chart) chart.hidden = view !== 'chart';
    this.root.querySelectorAll('[data-s1-modal-tabs] [data-s1-modal-view]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.s1ModalView === view);
    });
  }

  setUnit(unit) {
    this.unit = unit;
    this.root.querySelectorAll('[data-s1-modal-unit]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.s1ModalUnit === unit);
    });
    this.root.querySelectorAll('[data-s1-modal-unit-label]').forEach((label) => {
      label.textContent = unit;
    });
    if (this.waistInput) this.waistInput.placeholder = unit === 'cm' ? 'e.g. 85' : 'e.g. 34';
    if (this.hipInput) this.hipInput.placeholder = unit === 'cm' ? 'e.g. 90' : 'e.g. 36';
  }

  /* Calculator and chart read the same rows, so a recommendation always
     lands in the row the chart shows for that measurement. Inches convert
     to cm first; the match runs on the full table (not the product-filtered
     one) so a shopper between stocked sizes still gets the honest answer —
     the select handler refuses sizes the product doesn't carry. */
  calculate() {
    let size = null;
    if (this.isBra) {
      const raw = this.braInput ? this.braInput.value.trim() : '';
      if (!raw) return;
      size = s1BraSizeFor(this.allRows, raw);
    } else {
      let w = parseFloat(this.waistInput ? this.waistInput.value : '');
      let h = parseFloat(this.hipInput ? this.hipInput.value : '');
      if (!(w > 0) && !(h > 0)) return;
      if (this.unit === 'in') {
        if (w > 0) w *= 2.54;
        if (h > 0) h *= 2.54;
      }
      size = s1SizeFromMeasurements(this.allRows, this.calculator, { waistCm: w > 0 ? w : 0, hipCm: h > 0 ? h : 0 });
    }
    if (!size) {
      if (this.noMatch) this.noMatch.hidden = false;
      return;
    }
    this.showRecommendation(size);
  }

  showRecommendation(size) {
    this.recommendation = size;
    const sizeEl = this.root.querySelector('[data-s1-modal-size]');
    if (sizeEl) sizeEl.textContent = this.recommendation;
    const selectSizeEl = this.root.querySelector('[data-s1-modal-select-size]');
    if (selectSizeEl) selectSizeEl.textContent = this.recommendation;
    const form = this.root.querySelector('[data-s1-modal-form]');
    const result = this.root.querySelector('[data-s1-modal-result]');
    if (form) form.hidden = true;
    if (result) result.hidden = false;
  }

  reset() {
    this.recommendation = null;
    if (this.waistInput) this.waistInput.value = '';
    if (this.hipInput) this.hipInput.value = '';
    if (this.braInput) this.braInput.value = '';
    if (this.calcBtn) this.calcBtn.disabled = true;
    if (this.noMatch) this.noMatch.hidden = true;
    const form = this.root.querySelector('[data-s1-modal-form]');
    const result = this.root.querySelector('[data-s1-modal-result]');
    if (form) form.hidden = false;
    if (result) result.hidden = true;
  }
}

/* ---- sock upsell ----
   1:1 port of the "Sock Upsell" card in BoxerBriefProductSectionV2.tsx,
   value-tier pricing path (the same checkout-mirroring tiers the rest of this
   section uses). Sock data is a Liquid-rendered JSON island; original price
   falls back to base / 0.76 when no compare-at, per the SPA. */

const SOCK_SIZE_ORDER = ['2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL'];

/* ---- GA4 merchandising attribution (theme -> dataLayer -> GTM -> GA4) ----
   Shopify's Customer Events sandbox (Analyzify) cannot see theme DOM, so
   custom surfaces like the sock upsell get no attribution at all. Emits ONLY
   ee_view_item_list / ee_select_item. Never emit add_to_cart / remove_from_cart /
   begin_checkout / purchase from the theme — Analyzify already sends those from
   the sandbox and duplicating them double-counts revenue. */
function s1GaPush(eventName, ecommerce) {
  const dl = (window.dataLayer = window.dataLayer || []);
  dl.push({ ecommerce: null }); /* clear prior ecommerce object (GTM hygiene) */
  dl.push({ event: eventName, analyzify_source: 's1-pdp', ecommerce });
}

class S1SockUpsell {
  constructor(root, store) {
    this.root = root;
    this.store = store;
    const dataEl = root.querySelector('[data-s1-upsell-data]');
    this.socks = dataEl ? JSON.parse(dataEl.textContent) : [];
    this.state = { enabled: false, index: 0, color: '', size: 'M', bundleIndex: 2 };
    this.bundles = Array.from(root.querySelectorAll('[data-s1-upsell-bundle]'))
      .map((btn) => parseInt(btn.dataset.count, 10));
    /* the set-nudge card renders fewer packs than the SPA's three: clamp the
       default (largest pack) onto whatever is actually rendered */
    if (this.state.bundleIndex >= this.bundles.length) this.state.bundleIndex = Math.max(0, this.bundles.length - 1);
    /* set-nudge card preselects the smallest pack (Single); the SPA card
       keeps its largest-pack default */
    if (root.hasAttribute('data-s1-upsell-default-first')) this.state.bundleIndex = 0;

    /* default colour + size = first available variant of the first sock */
    const first = this.socks[0];
    const firstAvailable = first && first.variants.filter((v) => v.available)[0];
    if (firstAvailable) {
      if (firstAvailable.color) this.state.color = firstAvailable.color;
      if (firstAvailable.size) this.state.size = firstAvailable.size;
    }

    this.bind();
    this.render();
    this.initGa();
  }

  get sock() { return this.socks[this.state.index]; }

  gaListFields() {
    return {
      item_list_id: this.root.dataset.gaListId || '',
      item_list_name: this.root.dataset.gaListName || '',
    };
  }

  /* GA4 wants the list fields at the ecommerce level AND repeated on every item.
     item_id must be shopify_{REGION}_{productId}_{variantId} to reconcile with
     Analyzify's catalog; item_name is the real product title for the same reason. */
  gaItem(sock, index, variant) {
    const region = this.root.dataset.gaRegion || 'AU';
    const v = variant || sock.variants.filter((x) => x.available)[0] || sock.variants[0];
    const list = this.gaListFields();
    return {
      item_id: `shopify_${region}_${sock.id}_${v ? v.id : ''}`,
      item_name: sock.title,
      price: v ? v.price / 100 : 0,
      index: index + 1,
      item_list_id: list.item_list_id,
      item_list_name: list.item_list_name,
    };
  }

  /* Impression: once per list per PAGE VIEW, gated on half visibility. The
     dedupe lives on window because a fit switch re-renders the section and
     constructs a fresh instance — an instance flag would re-fire the view. */
  initGa() {
    const key = this.root.dataset.gaListId;
    if (!key || !this.socks.length) return;
    const fire = () => {
      const seen = (window.__s1GaViewed = window.__s1GaViewed || {});
      if (seen[key]) return;
      seen[key] = true;
      const list = this.gaListFields();
      s1GaPush('ee_view_item_list', {
        ...list,
        items: this.socks.map((s, i) => this.gaItem(s, i)),
      });
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
          io.disconnect();
          fire();
        });
      }, { threshold: 0.5 });
      io.observe(this.root);
    } else {
      fire();
    }
  }

  /* Selection = the shopper opting a sock into the order (toggle on) or
     swapping style while opted in. Colour/size/bundle refine the same
     product, and browsing styles while toggled off is not a selection —
     counting either would inflate select_item and wreck list CTR. */
  gaSelect() {
    if (!this.root.dataset.gaListId || !this.sock) return;
    const v = this.currentVariant();
    if (!v || !v.available) return; /* sold out: render() forces the toggle back off */
    s1GaPush('ee_select_item', {
      ...this.gaListFields(),
      items: [this.gaItem(this.sock, this.state.index, v)],
    });
  }

  /* currentUpsellVariant: colour+size match -> colour match -> first variant,
     available variants first, same as the SPA memo */
  currentVariant() {
    const sock = this.sock;
    if (!sock) return null;
    const { color, size } = this.state;
    const avail = sock.variants.filter((v) => v.available);
    return avail.find((v) => v.color === color && v.size === size)
      || avail.find((v) => v.color === color)
      || sock.variants[0]
      || null;
  }

  /* availability reconciliation effect: keep colour if any available variant
     still has it, else first available colour; then snap size onto that
     colour's available sizes */
  reconcile() {
    const sock = this.sock;
    if (!sock) return;
    const avail = sock.variants.filter((v) => v.available);
    if (!avail.length) return;
    const colorOk = avail.some((v) => v.color === this.state.color);
    if (!colorOk) this.state.color = avail[0].color;
    const targetColor = colorOk ? this.state.color : avail[0].color;
    const sizes = avail.filter((v) => v.color === targetColor).map((v) => v.size);
    if (!sizes.includes(this.state.size) && sizes[0]) this.state.size = sizes[0];
  }

  /* pricing in DOLLARS, mirroring the SPA exactly: originalPrice is
     compareAt when present, else Math.round(base / 0.76) — rounded to whole
     dollars BEFORE the tier discount is applied (so $14 -> $18 -> $16.20) */
  originalPriceDollars(v) {
    const baseCents = (v && v.price) || (this.sock && this.sock.variants[0] && this.sock.variants[0].price) || 0;
    const compareCents = v && v.compareAt;
    return compareCents ? compareCents / 100 : Math.round(baseCents / 100 / 0.76);
  }

  tierPct(projectedDollars) { return this.store.valueTierPct(Math.round(projectedDollars * 100)); }

  items() {
    const v = this.currentVariant();
    /* a fully sold-out sock falls through currentVariant() to variants[0];
       adding that id would 422 the WHOLE multi-item /cart/add.js request and
       block the main product too — never ride an unavailable variant along */
    if (!this.state.enabled || !v || !v.available) return [];
    return [{ id: v.id, quantity: this.bundles[this.state.bundleIndex] }];
  }

  snapshot() { return { ...this.state }; }

  restore(snap) {
    if (!snap) return;
    this.state = { ...this.state, ...snap };
    if (!this.socks[this.state.index]) this.state.index = 0;
    if (this.state.bundleIndex >= this.bundles.length) this.state.bundleIndex = Math.max(0, this.bundles.length - 1);
    this.reconcile();
    this.render();
  }

  bind() {
    this.root.addEventListener('click', (e) => {
      if (e.target.closest('[data-s1-upsell-toggle]')) {
        this.state.enabled = !this.state.enabled;
        if (this.state.enabled) this.gaSelect();
        return this.render();
      }
      const bundleBtn = e.target.closest('[data-s1-upsell-bundle]');
      if (bundleBtn) {
        this.state.bundleIndex = parseInt(bundleBtn.dataset.s1UpsellBundle, 10);
        return this.render();
      }
    });
    this.root.querySelector('[data-s1-upsell-style]')?.addEventListener('change', (e) => {
      this.setStyle(parseInt(e.target.value, 10));
    });
    /* set-nudge card: style tabs + colour tiles instead of selects */
    this.root.addEventListener('click', (e) => {
      const styleBtn = e.target.closest('[data-s1-upsell-style-btn]');
      if (styleBtn) return this.setStyle(parseInt(styleBtn.dataset.s1UpsellStyleBtn, 10));
      const colorBtn = e.target.closest('[data-s1-upsell-color-btn]');
      if (colorBtn) {
        this.state.color = colorBtn.dataset.s1UpsellColorBtn;
        this.reconcile();
        return this.render();
      }
      const sizeBtn = e.target.closest('[data-s1-upsell-size-btn]');
      if (sizeBtn) {
        this.state.size = sizeBtn.dataset.s1UpsellSizeBtn;
        return this.render();
      }
    });
    this.root.querySelector('[data-s1-upsell-color]')?.addEventListener('change', (e) => {
      this.state.color = e.target.value;
      this.reconcile();
      this.render();
    });
    this.root.querySelector('[data-s1-upsell-size]')?.addEventListener('change', (e) => {
      this.state.size = e.target.value;
      this.render();
    });
  }

  setStyle(index) {
    if (!this.socks[index]) return;
    this.state.index = index;
    this.reconcile();
    if (this.state.enabled) this.gaSelect();
    this.render();
  }

  /* set-nudge card: tabs (style) and image tiles (colour) built as DOM nodes
     like renderOptions; the whole group is rebuilt only when its option set
     changes, selection is a class/aria toggle */
  renderChoices(container, options, selected, attr, className) {
    if (!container) return;
    const key = JSON.stringify(options);
    if (container._s1Options !== key) {
      container._s1Options = key;
      container.textContent = '';
      for (const o of options) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = className;
        btn.setAttribute(attr, String(o.value));
        if (o.image) {
          const img = document.createElement('img');
          img.src = o.image;
          img.alt = o.label;
          img.loading = 'lazy';
          img.width = 128;
          img.height = 128;
          btn.appendChild(img);
          btn.title = o.label;
        } else {
          btn.textContent = o.label;
        }
        container.appendChild(btn);
      }
    }
    container.querySelectorAll(`[${attr}]`).forEach((btn) => {
      const on = btn.getAttribute(attr) === String(selected);
      btn.setAttribute('aria-pressed', String(on));
    });
  }

  /* options are merchant product data - build DOM nodes instead of HTML
     strings so quotes/angle brackets in titles can't inject attributes */
  renderOptions(select, options, selected) {
    if (!select) return;
    const key = JSON.stringify(options);
    if (select._s1Options !== key) {
      select._s1Options = key;
      select.textContent = '';
      for (const o of options) {
        const opt = document.createElement('option');
        opt.value = String(o.value);
        opt.textContent = o.label;
        select.appendChild(opt);
      }
    }
    select.value = String(selected);
  }

  /* Set-nudge line: distance from the NEXT value tier above (cart + the main
     product at its selected quantity), with the opted-in socks counted at
     their real variant price (what cart.original_total_price will hold — the
     display "original" above is the SPA's compare-at derivation and would
     overstate the projection). Hidden when no higher tier exists. */
  renderNudge(v, count) {
    const el = this.root.querySelector('[data-s1-upsell-nudge]');
    if (!el || !this.store.state) return;
    const wrap = this.root.querySelector('[data-s1-upsell-nudge-wrap]');
    const qty = this.store.state.tierQty || 1;
    const base = this.store.cartSubtotal + qty * this.store.currentPricing().price;
    const basePct = this.store.valueTierPct(base);
    let next = null;
    for (const t of this.store.data.valueTiers || []) {
      if (t.threshold > base && t.pct > basePct && (!next || t.threshold < next.threshold)) next = t;
    }
    const titleEl = this.root.querySelector('[data-s1-upsell-nudge-title]');
    const show = (text, unlocked, titleKey) => {
      if (wrap) { wrap.hidden = false; wrap.classList.toggle('is-unlocked', unlocked); }
      el.textContent = text;
      if (titleEl) titleEl.textContent = titleEl.dataset['s1Title' + titleKey] || titleEl.dataset.s1TitleLocked || '';
    };
    const fill = (fmt, amount, pct) => (fmt || '')
      .replace('{amount}', String(amount))
      .replace('{pct}', String(pct));

    /* only ask for a top-up this card can actually deliver: the biggest
       pack of the selected sock. A $198 ask from a three-pair card is noise. */
    const sockPrice = (v && v.price) || (this.sock && this.sock.variants[0] && this.sock.variants[0].price) || 0;
    const reach = Math.max(...this.bundles, 0) * sockPrice;
    if (next && next.threshold - base <= reach) {
      const sockCents = this.state.enabled && v && v.available ? count * v.price : 0;
      const gap = next.threshold - (base + sockCents);
      const unlocked = gap <= 0;
      return show(fill(unlocked ? el.dataset.s1NudgeUnlocked : el.dataset.s1NudgeAdd, Math.ceil(gap / 100), next.pct), unlocked, unlocked ? 'Unlocked' : 'Locked');
    }
    /* next tier out of reach: confirm the tier the order already earns,
       or say nothing at all */
    if (basePct > 0) return show(fill(el.dataset.s1NudgeApplied, 0, basePct), true, 'Applied');
    if (wrap) wrap.hidden = true;
  }

  render() {
    const sock = this.sock;
    if (!sock) { this.root.hidden = true; return; }
    const v = this.currentVariant();
    const q = (sel) => this.root.querySelector(sel);

    /* every variant of the selected sock sold out: force the toggle off and
       disable it so items() can never be asked to ride an unavailable id */
    const soldOut = !(v && v.available);
    if (soldOut) this.state.enabled = false;

    this.root.classList.toggle('is-enabled', this.state.enabled);
    this.root.classList.toggle('is-soldout', soldOut);
    const toggle = q('[data-s1-upsell-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-checked', String(this.state.enabled));
      toggle.disabled = soldOut;
    }

    const img = q('[data-s1-upsell-img]');
    if (img) {
      const src = (v && v.image) || sock.image || '';
      if (img.getAttribute('src') !== src) img.src = src;
    }

    /* {symbol}{formatPrice(Math.round(dollars))}, same as the SPA */
    const displayDollars = (dollars) => moneyFormat(Math.round(dollars) * 100);
    const original = this.originalPriceDollars(v);
    const count = this.bundles[this.state.bundleIndex];
    this.renderNudge(v, count);
    if (this.root.hasAttribute('data-s1-upsell-project-main')) {
      const extra = this.state.enabled && v && v.available ? count * v.price : 0;
      if (extra !== this.store.extraCents) {
        this.store.extraCents = extra;
        /* re-price the set (header, packs, CTA); the store's emit calls
           back into render(), which lands here with extra unchanged */
        this.store.emit('upsell');
      }
    }
    /* the set-nudge card sells the socks as the thing that tips THIS order
       over the tier, so its per-pair prices project against cart + the main
       product at its selected quantity (the SPA card projects cart-only) */
    let mainCents = 0;
    if (this.root.hasAttribute('data-s1-upsell-project-main') && this.store.state) {
      mainCents = (this.store.state.tierQty || 1) * this.store.currentPricing().price;
    }
    const cartTotal = (this.store.cartSubtotal + mainCents) / 100;
    const pct = this.tierPct(cartTotal + count * original);
    const perItem = original * (1 - pct / 100);

    const priceEl = q('[data-s1-upsell-price]');
    if (priceEl) priceEl.textContent = displayDollars(perItem);
    const compareEl = q('[data-s1-upsell-compare]');
    if (compareEl) {
      compareEl.hidden = perItem >= original;
      compareEl.textContent = displayDollars(original);
    }

    /* "{tier} APPLIED" pill from the tier the cart is already in */
    const currentPct = this.tierPct(cartTotal);
    const badge = q('[data-s1-upsell-badge]');
    if (badge) {
      badge.hidden = currentPct <= 0;
      badge.textContent = `${currentPct}% off APPLIED`;
    }

    const styleOptions = this.socks.map((s, i) => ({
      value: i,
      label: s.title.replace(/Unisex\s*|Sock\s*/gi, '').trim(),
    }));
    this.renderOptions(q('[data-s1-upsell-style]'), styleOptions, this.state.index);
    this.renderChoices(q('[data-s1-upsell-style-tabs]'), styleOptions, this.state.index,
      'data-s1-upsell-style-btn', 's1-pdp__setnudge-tab');

    const avail = sock.variants.filter((va) => va.available);
    const colors = [...new Set(avail.map((va) => va.color))];
    this.renderOptions(q('[data-s1-upsell-color]'), colors.map((c) => ({ value: c, label: c })), this.state.color);
    this.renderChoices(q('[data-s1-upsell-color-tiles]'), colors.map((c) => {
      const withImg = avail.find((va) => va.color === c && va.image);
      return { value: c, label: c, image: (withImg && withImg.image) || sock.image || '' };
    }), this.state.color, 'data-s1-upsell-color-btn', 's1-pdp__setnudge-tile');

    const sizes = [...new Set(avail.filter((va) => va.color === this.state.color).map((va) => va.size))]
      .sort((a, b) => SOCK_SIZE_ORDER.indexOf(a) - SOCK_SIZE_ORDER.indexOf(b));
    const sizeOptions = sizes.map((s) => ({ value: s, label: s }));
    this.renderOptions(q('[data-s1-upsell-size]'), sizeOptions, this.state.size);
    /* pills show the short size code (Small -> S); the value stays the
       variant's real option text so currentVariant() keeps matching */
    const shortSize = (name) => String(name)
      .replace(/^extra\s*small$/i, 'XS').replace(/^small$/i, 'S').replace(/^medium$/i, 'M')
      .replace(/^large$/i, 'L').replace(/^extra\s*large$/i, 'XL');
    this.renderChoices(q('[data-s1-upsell-size-pills]'), sizeOptions.map((o) => ({ value: o.value, label: shortSize(o.label) })),
      this.state.size, 'data-s1-upsell-size-btn', 's1-pdp__setnudge-size-pill');

    this.root.querySelectorAll('[data-s1-upsell-bundle]').forEach((btn, i) => {
      btn.classList.toggle('is-selected', i === this.state.bundleIndex);
      btn.setAttribute('aria-pressed', String(i === this.state.bundleIndex));
      const bCount = this.bundles[i];
      const bPct = this.tierPct(cartTotal + bCount * original);
      const bPerItem = original * (1 - bPct / 100);
      const priceSpan = btn.querySelector('[data-s1-upsell-bundle-price]');
      if (priceSpan) priceSpan.textContent = `${displayDollars(bPerItem)} ea.`;
      const unlock = btn.querySelector('[data-s1-upsell-bundle-unlock]');
      if (unlock) {
        const unlocks = bPct > 0 && bPct > currentPct;
        unlock.hidden = !unlocks;
        if (unlocks) unlock.textContent = `\u{1F511} ${bPct}% off`;
      }
    });
  }
}

class S1Pdp extends HTMLElement {
  connectedCallback() {
    if (this._initialized) {
      /* re-connect without replacement (theme editor drag etc.): restore
         everything disconnectedCallback tore down */
      document.documentElement.addEventListener('cart:change', this.onCartChange);
      if (this.onProductChange) document.addEventListener('s1:product-change', this.onProductChange);
      this._abort = new AbortController();
      (this._resizeChecks || []).forEach((check) => {
        window.addEventListener('resize', check, { signal: this._abort.signal });
        check();
      });
      this.initUgc();
      this.initAthletes();
      return;
    }
    this._initialized = true;
    this._abort = new AbortController();
    this._resizeChecks = [];
    const dataEl = this.querySelector('[data-s1-pdp-data]');
    if (!dataEl) return;
    this.store = new S1PdpStore(JSON.parse(dataEl.textContent));
    this.store.subscribe(() => this.render());

    /* a preselected bundle default needs the server resolution in remote
       mode (local mode reads its lookup table synchronously) */
    if (this.store.bundleMode && this.store.data.bundleRemote
        && this.store.state.bundle.some((v) => v != null)) {
      this.refreshBundleState();
    }

    /* NiteKnit bundle rows: the SPA's Bundle Selector is ALWAYS available —
       it never consults tags and works for any colour count. Two store
       gates would silently kill it, so the block's presence lifts both:
       1. no-tier / nopacks tags clamp setTier to 1 (packsDisabled) — the
          sleepwear product carries the tag for the legacy pack logic;
       2. slotMode requires >1 colours, but the per-set drawer only needs
          the colour machinery, which works from a single colour too.
       Gated on the block so every other template keeps both behaviours.
       The NiteKnit segmented tier selector (socks-new look on the sleep
       template) needs the same lift: the product still carries no-tier. */
    if (this.querySelector('[data-s1-bundle-rows]')
        || (this.classList.contains('s1-pdp--nk') && this.querySelector('[data-s1-tier-selector]'))) {
      this.store.data.current.packsDisabled = false;
      /* the constructor clamped tierQty to 1 while packsDisabled was still
         set; restore the configured default (a migrate snapshot, applied
         below, still wins) */
      const tiers = this.store.data.tiers || [];
      const intended = (tiers[this.store.data.defaultTierIndex] || tiers[0] || {}).count || 1;
      /* setTier (not a raw assignment) so a single-mode allocation follows the
         restored quantity — a no-tier-tagged product without colours (XCup)
         otherwise wakes up with alloc {current:1} vs tierQty 2 and a CTA
         that can never enable */
      if (this.store.state.tierQty !== intended) this.store.setTier(intended);
      if (!this.store.slotMode) {
        const colourKeys = Object.keys(this.store.data.current.colours || {});
        if (colourKeys.length >= 1) {
          this.store.slotMode = true;
          /* singleColour pins effectiveMode to 'single', which would keep
             the drawer shut — the bundle pack IS the mixed mode here */
          this.store.singleColour = false;
          if (!this.store.state.colour) this.store.state.colour = colourKeys[0];
          this.store.state.mode = 'single';
          /* re-derive the default size with the slot-mode rule (first size in
             stock), matching what the constructor would have picked */
          const sizes = this.store.data.current.sizes || {};
          const slotDefault = Object.keys(sizes).find((k) => sizes[k].available) || null;
          if (slotDefault) this.store.state.size = slotDefault;
        }
      }
    }

    /* Sibling swatch row without a colour grid (Girls Shortie): the mixed
       builder has no UI here, so mixed mode could never complete a pack —
       pin the store to single mode like a one-colour family. Tile taps
       still switch the previewed product; the allocation follows tierQty. */
    if (this.querySelector('[data-s1-scolours]') && !this.querySelector('[data-s1-colour-grid]')) {
      this.store.singleColour = true;
      this.store.setMode('single');
    }

    /* Selector-only colour strip with NO mixed-pack UI (State of Origin
       landing: colour picker + tiers, no slot builder, no bundle rows):
       every pack is single-colour × qty — the SPA's mixed machinery is dead
       code on those pages. Without this pin, picking a multi-pack flips the
       store to mixed with nothing to complete the allocation and the CTA
       deadlocks at 1/N. */
    if (this.querySelector('[data-s1-selector-only]')
        && !this.querySelector('[data-s1-slot-builder]')
        && !this.querySelector('[data-s1-bundle-rows]')) {
      this.store.singleColour = true;
      this.store.setMode('single');
    }

    const migrate = window.__s1PdpMigrate;
    if (migrate) {
      window.__s1PdpMigrate = null;
      this.store.restore(migrate);
      /* bundle sizes ride the pack switch only where the section opts in */
      if (this.dataset.bundleKeepSizes !== undefined) this.store.restoreBundle(migrate.bundle);
      /* style & colour tile on ANOTHER fit product: the tapped colour wins
         over the migrated one (restore() carried the previous product's
         colour, which is only right for the plain fit-pill switch) */
      if (migrate.forceColour && this.store.slotMode && this.store.data.current.colours
          && this.store.data.current.colours[migrate.forceColour]) {
        this.store.state.colour = migrate.forceColour;
      }
      /* pushState changed the URL; keep the tab title / history entries in
         sync with the product now shown (crawlers see server-rendered heads).
         Embedded buy boxes stay on the landing page — its title stands. */
      if (this.store.data.docTitle && this.dataset.embedded === undefined) {
        document.title = this.store.data.docTitle;
      }
    } else {
      /* legacy deep links (?pack-qty=N): the pre-migration pack pages
         preselected the quantity from the URL (custom.js), and marketing
         links may still carry it. Honour it on fresh loads only — in-page
         switches keep the query string, and re-applying would stomp a
         quantity the shopper changed since landing. setTier clamps no-tier
         products back to 1. */
      const packQty = parseInt(new URLSearchParams(window.location.search).get('pack-qty'), 10);
      if (packQty > 0) this.store.setTier(packQty);

      /* embedded deep link (?product=handle): the island iframe forwarded a
         product param and the SPA replaceState'd it on every colour switch —
         honour it the same way on fresh loads of an embedded buy box. Soft:
         a stale/mistyped handle must not hard-navigate off the landing page */
      if (this.dataset.embedded !== undefined) {
        const bootParams = new URLSearchParams(window.location.search);
        const deepHandle = bootParams.get('product');
        if (deepHandle && deepHandle !== this.store.state.selectedHandle) {
          /* ?collection= scopes the fetch to the right colour family (written
             by the embedded replaceState on every switch). URLs without it
             fall back to the SPA's getCollectionFromHandle substring
             heuristic — but only when the guess is a family this section
             actually configures (fit1-4 / extras), so a womens landing can
             never be steered into the mens collections. Handles are the AU
             store's; UK/US just never match their allow-lists (harmless). */
          let bootColl = bootParams.get('collection') || undefined;
          if (!bootColl) {
            const h = deepHandle.toLowerCase();
            let guess;
            if (h.includes('sport')) guess = 'sports-underwear';
            else if (h.includes('fly')) guess = 'boxer-brief-fly';
            else if (h.includes('junior')) guess = 'junior-trunks';
            else if (h.includes('trunk')) guess = 'trunk';
            else if (h.includes('brief') && !h.includes('boxer')) guess = 'briefs';
            else if (h.includes('boxer')) guess = 'boxer-brief';
            const allowed = (this.dataset.fitAllowed || '').split(',').filter(Boolean);
            if (guess && allowed.includes(guess)) bootColl = guess;
          }
          this.switchProduct(`/products/${encodeURIComponent(deepHandle)}`, this.store.snapshot(), bootColl, { soft: true });
        }
      }
    }

    /* strip order is anchored to the product the session STARTED on (SPA
       sorts once by initialHandle); switches must not reshuffle tiles */
    this.stripFirstHandle = (migrate && migrate.firstHandle) || this.store.state.selectedHandle;
    const strip = this.querySelector('[data-s1-colour-strip]');
    if (strip) {
      const anchorTile = strip.querySelector(`[data-s1-colour="${CSS.escape(this.stripFirstHandle)}"]`);
      if (anchorTile) {
        if (anchorTile !== strip.firstElementChild) strip.prepend(anchorTile);
        /* prepending into a scroll container triggers scroll anchoring: the
           browser bumps scrollLeft by one tile to keep the OLD first tile in
           view, hiding the freshly-fronted current colour off-screen left —
           pin the strip back to its start on fresh loads (migrations restore
           the shopper's own scroll position instead) */
        if (migrate && typeof migrate.stripScroll === 'number') strip.scrollLeft = migrate.stripScroll;
        else strip.scrollLeft = 0;
      } else {
        this.stripFirstHandle = this.store.state.selectedHandle;
        const currentTile = strip.querySelector(`[data-s1-colour="${CSS.escape(this.stripFirstHandle)}"]`);
        if (currentTile && currentTile !== strip.firstElementChild) strip.prepend(currentTile);
        strip.scrollLeft = 0;
      }
    }

    /* mix-collection builder strips re-order by XS/S/M stock, most to least
       (SPA sortProductsByStock over WOMENS_TARGET_SIZES). CONFIRMED in the
       deployed bundle: minification renames the function, but the section
       chunk imports `{s as xs, W as hs}` from product-sorting and calls
       xs(r,hs) after every collection fetch — never trust a grep for
       unminified identifiers. SSR emits collection order; one stable DOM
       sort per load, scored via resolveSizeKey (SKU-size equivalent). */
    if (this.store.collectionSlots) {
      const score = (handle) => {
        const p = this.store.products[handle];
        if (!p) return -1;
        let n = 0;
        for (const t of S1PdpStore.WOMENS_TARGET_SIZES) {
          const key = this.store.resolveSizeKey(p, t);
          if (key && p.sizes[key].available) n += 1;
        }
        return n;
      };
      /* SPA secondary criterion is product availableForSale; slim island
         entries derive it from the sizes map (any size in stock) */
      const anyAvail = (handle) => {
        const p = this.store.products[handle];
        return p && Object.values(p.sizes).some((sv) => sv.available) ? 1 : 0;
      };
      this.querySelectorAll('[data-s1-slot-builder] [data-s1-builder-strip] .s1-pdp__builder-strip').forEach((stripEl) => {
        const tiles = Array.from(stripEl.querySelectorAll('[data-s1-builder-add]'));
        tiles
          .map((tile, idx) => ({ tile, idx, s: score(tile.dataset.s1Handle), a: anyAvail(tile.dataset.s1Handle) }))
          .sort((a, b) => (b.s - a.s) || (b.a - a.a) || (a.idx - b.idx))
          .forEach((entry) => stripEl.appendChild(entry.tile));
      });
    }

    const modalEl = this.querySelector('[data-s1-size-modal]');
    if (modalEl) {
      /* SPA onSelectSize keeps the current colour and swaps the size; the
         store's setSize is that operation. Alias resolution ('S' <-> 'Small')
         matches restore()'s handling of external size values. A recommendation
         the product simply doesn't stock (calculator says 2XL, range stops at
         XL) must not be written into state — it would clear the allocation
         and dead-end the CTA on a size no pill can ever re-select. */
      this.sizeModal = new S1SizeModal(modalEl, (size) => {
        const key = this.store.resolveSizeKey(this.store.data.current, size);
        if (key) this.store.setSize(key);
      });
    }

    this.bundleModals = {};

    const upsellEl = this.querySelector('[data-s1-upsell]');
    if (upsellEl) {
      this.sockUpsell = new S1SockUpsell(upsellEl, this.store);
      this.store.upsellItems = () => this.sockUpsell.items();
      if (migrate && migrate.upsell) this.sockUpsell.restore(migrate.upsell);
    }

    /* a selection-card switch replaced the element mid-scroll; after layout
       settles, correct the landing so the section top (the divider) sits
       exactly below the sticky header */
    if (window.__s1PdpScrollAfterSwitch) {
      window.__s1PdpScrollAfterSwitch = false;
      /* converge in a few passes: late layout shifts (images, fonts) between
         passes are absorbed until the divider sits exactly under the header */
      const settle = () => {
        const margin = parseFloat(getComputedStyle(this).scrollMarginTop) || 0;
        const top = Math.max(0, this.getBoundingClientRect().top + window.scrollY - margin);
        if (Math.abs(window.scrollY - top) > 4) window.scrollTo({ top, behavior: 'smooth' });
      };
      [100, 400, 900].forEach((ms) => setTimeout(settle, ms));
    }

    /* keyboard path into the fullscreen viewer (the gallery div is
       role=button tabindex=0) */
    const galleryEl = this.querySelector('[data-s1-gallery]');
    if (galleryEl) {
      galleryEl.addEventListener('keydown', (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === galleryEl) {
          e.preventDefault();
          this.toggleFullscreen(true);
        }
      });
    }

    this.bindEvents();
    this.initGallery();
    this.initFsZoom();
    this.initStickyAtc();
    /* variant-colour families open on the default colour's variant image,
       not media[0] — the SPA syncs the gallery to selectedVariant on load
       and after every style switch (:440-454). Runs after initGallery so
       the track/thumb state is ready. */
    /* with static media the SPA stays on the first static on a fresh load
       and only jumps to the variant image after an in-page product switch
       (prevProductHandleRef null -> stay) */
    /* gallery_open_first: a fresh load keeps media 1 (product video) as the
       hero; only colour taps and in-page switches jump to the colour image */
    const openFirst = this.dataset.s1OpenFirst !== undefined && !migrate;
    if (this.store.slotMode && this.store.state.colour && !openFirst && (migrate || !this.galleryStaticCount)) {
      const c = this.store.data.current.colours[this.store.state.colour];
      if (c && typeof c.mediaIndex === 'number' && c.mediaIndex >= 0 && !this.galleryGroupSize) {
        const track = this.querySelector('[data-s1-gallery-track]');
        if (track) track.classList.add('is-jump');
        this.setGalleryIndex((this.galleryStaticCount || 0) + c.mediaIndex);
        if (track) {
          void track.offsetWidth;
          track.classList.remove('is-jump');
        }
      }
    }
    /* section swapped in-page (style tile / fit pill): the new gallery mounts
       hidden and fades in on the right slide — same crossfade as a colour pick */
    if (migrate) {
      const gallery = this.querySelector('[data-s1-gallery]');
      if (gallery) {
        gallery.classList.add('is-swapping', 'is-mounting');
        /* timers, not rAF: background/hidden tabs throttle rAF and would
           leave the gallery invisible until the tab is focused */
        setTimeout(() => {
          gallery.classList.remove('is-swapping');
          setTimeout(() => gallery.classList.remove('is-mounting'), 250);
        }, 40);
      }
    }
    /* colour-grouped gallery opens on the default colour's chunk */
    this.syncGalleryGroup();
    /* NiteKnit bundle rows: keep the slot pack consistent with the current
       product. Fresh load with a >1 default tier seeds the pack; a fit/style
       switch whose migrated slots point at the previous product re-seeds for
       the new one (pack size survives, per-set choices reset — the SPA has
       no cross-product switching to mirror here). */
    if (this.querySelector('[data-s1-bundle-rows]') && this.store.slotMode) {
      const s = this.store.state;
      const stale = s.slots.some((slot) => slot.handle !== s.selectedHandle);
      if ((s.tierQty > 1 && s.slots.length === 0) || stale) {
        this.store.setBundleCount(s.tierQty);
      }
    }
    /* drawer-less bundle rows (XCup hide_drawer): the slots carry the pack's
       sizes but there is NO UI to see or edit them, so a size change after
       picking a pack must re-seed every slot to the new size — otherwise a
       hidden slot keeps the old size and buildItems ships the wrong variant.
       Templates WITH a drawer keep NiteKnit's semantics (slots are the
       user's explicit per-set choices; the drawer shows them). */
    if (this.querySelector('[data-s1-bundle-rows]')
        && !this.querySelector('[data-s1-bundle-drawer]')
        && this.store.slotMode) {
      this.store.subscribe((type) => {
        if (type === 'size'
            && this.store.effectiveMode === 'mixed'
            && this.store.state.slots.length) {
          this.store.setBundleCount(this.store.state.tierQty);
        }
      });
    }
    this.initStepCarousels();
    this.initGridDrawer();
    /* keep the grid drawer open across an in-page product switch (the SPA
       drawer survives because its component never unmounts) */
    if (migrate && migrate.drawerOpen) this.toggleDrawer(true);
    this.store.refreshSubtotal();

    this.onCartChange = () => this.store.refreshSubtotal();
    document.documentElement.addEventListener('cart:change', this.onCartChange);

    this.onProductChange = (e) => {
      e.preventDefault();
      this.switchProduct(e.detail.url, this.store.snapshot(), e.detail.collection);
    };
    document.addEventListener('s1:product-change', this.onProductChange);

    /* the path this instance rendered for — popstate compares against it so
       only a real product-to-product history move forces a reload */
    window.__s1PdpCurrentPath = window.location.pathname;
    if (!window.__s1PdpPopstate) {
      window.__s1PdpPopstate = true;
      window.addEventListener('popstate', () => {
        /* only react to history we created — unrelated pushState users (cart
           drawer, filters) fire popstate too, but they don't change the
           product pathname, so those must not force a reload */
        if (window.__s1PdpPushed
            && document.querySelector('s1-pdp')
            && window.location.pathname !== window.__s1PdpCurrentPath) {
          window.location.reload();
        }
      });
    }

    this.initColourVote();
    this.initUgc();
    this.initAthletes();
    this.initKlaviyo();
    this.initSwitcherPosition();

    /* role=button divs (USP chips) don't synthesize clicks on Enter/Space */
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const chip = e.target.closest && e.target.closest('[role="button"][data-s1-action="scroll-to"]');
      if (chip) {
        e.preventDefault();
        chip.click();
      }
    });

    /* slot builder: per-slot size <select> changes flow through a delegated
       change listener (click delegation can't see them) */
    this.addEventListener('change', (e) => {
      const slotSelect = e.target.closest('[data-s1-slot-size]');
      if (slotSelect) this.store.setSlotSize(parseInt(slotSelect.dataset.s1SlotSize, 10), slotSelect.value);
    });

    this.render();
  }

  /* ---- variant-colour UI (socks): swatch row, slot builder, colour vote ---- */

  /* colour pick shared by the swatch row and the v2 style tiles. SPA syncs
     the gallery to the selected colour: grouped galleries (NiteKnit) swap to
     the colour's chunk, others jump to the variant image (offset past any
     static prepended media) */
  pickColour(name) {
    this.store.setColour(name);
    if (this.galleryGroupSize) {
      this.syncGalleryGroup();
      return;
    }
    const c = this.store.data.current.colours && this.store.data.current.colours[name];
    if (c && typeof c.mediaIndex === 'number' && c.mediaIndex >= 0) {
      this.jumpGallery((this.galleryStaticCount || 0) + c.mediaIndex);
    }
  }

  /* Colour/style change: a quick crossfade straight to the target slide,
     instead of sliding the track across every image in between. Matches
     the feel of the cross-style path (section swap, fade-in on mount) so
     picking a colour and picking another style look like the same gesture. */
  jumpGallery(i) {
    const gallery = this.querySelector('[data-s1-gallery]');
    const track = this.querySelector('[data-s1-gallery-track]');
    if (!gallery || !track || i === this.galleryIndex) return;
    if (this._jumpTimer) clearTimeout(this._jumpTimer);
    gallery.classList.add('is-swapping');
    this._jumpTimer = setTimeout(() => {
      track.classList.add('is-jump');
      this.setGalleryIndex(i);
      /* flush the transform with transitions off, then re-enable them */
      void track.offsetWidth;
      track.classList.remove('is-jump');
      gallery.classList.remove('is-swapping');
      this._jumpTimer = null;
    }, 160);
  }

  /* Socks v2 style & colour tiles: pressed = current product + selected
     colour; head reads "CREW / WHITE" from the pressed tile's label */
  renderStyleTiles() {
    const wrap = this.querySelector('[data-s1-style-colour]');
    if (!wrap) return;
    const s = this.store.state;
    let pressedLabel = null;
    wrap.querySelectorAll('[data-s1-style-tile]').forEach((tile) => {
      const sameProduct = tile.dataset.s1Handle === s.selectedHandle;
      const on = sameProduct && (!tile.dataset.s1ColourName || tile.dataset.s1ColourName === s.colour);
      tile.setAttribute('aria-pressed', String(on));
      if (on) pressedLabel = tile.dataset.s1StyleLabel || null;
      /* current product: dim the colour when the SELECTED size is out of
         stock in it (same rule the swatch strip + builder tiles use) */
      if (sameProduct && tile.dataset.s1ColourName && s.size && this.store.slotMode) {
        const v = this.store.colourVariant(s.selectedHandle, tile.dataset.s1ColourName, s.size);
        tile.classList.toggle('is-soldout', !(v && v.available));
      }
    });
    const nameEl = wrap.querySelector('[data-s1-style-name]');
    if (nameEl && pressedLabel) nameEl.textContent = pressedLabel;
  }

  /* Socks v2 segmented tier readout: selected pack's unit + next-tier nudge */
  renderTierReadout() {
    const unitEl = this.querySelector('[data-s1-tier-unit]');
    if (!unitEl) return;
    const qty = this.store.state.tierQty;
    const unit = this.store.unitPriceCents(qty);
    const base = this.store.currentPricing().price;
    unitEl.textContent = moneyFormatCents(unit);
    /* discounted pack: coloured unit + struck base price beside it */
    unitEl.classList.toggle('is-discounted', unit < base);
    const strike = this.querySelector('[data-s1-tier-strike]');
    if (strike) {
      strike.hidden = !(unit < base);
      strike.textContent = moneyFormatCents(base);
    }
    const nudge = this.querySelector('[data-s1-tier-nudge]');
    if (nudge) {
      const next = this.store.nextValueTier(qty);
      nudge.hidden = !next;
      if (next) {
        const fmt = nudge.dataset.s1NudgeFormat || 'Add ${amount} more to unlock {pct}% off';
        nudge.textContent = fmt.replaceAll('{amount}', String(Math.ceil(next.gapCents / 100))).replaceAll('{pct}', String(next.pct));
      }
    }
    const link = this.querySelector('[data-s1-builder-open]');
    if (link) {
      const mixed = this.store.effectiveMode === 'mixed';
      link.setAttribute('aria-pressed', String(mixed));
      link.classList.toggle('is-active', mixed);
      /* mixed is impossible at a single pair (setMode pins single) */
      link.disabled = qty <= 1 || !!this.store.packsDisabled;
      const label = link.querySelector('[data-s1-builder-open-label]');
      if (label) label.textContent = mixed ? (label.dataset.s1CloseText || 'Single style') : (label.dataset.s1OpenText || label.textContent);
    }
  }

  renderVariantColours() {
    const wrap = this.querySelector('[data-s1-vcolours]');
    if (!wrap || !this.store.slotMode) return;
    const nameEl = wrap.querySelector('[data-s1-vcolour-name]');
    if (nameEl) nameEl.textContent = this.store.state.colour || '';
    wrap.querySelectorAll('[data-s1-vcolour]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.s1Vcolour === this.store.state.colour));
    });
  }

  /* absorbency cards (period underwear): pressed state follows the store;
     a level whose every size is out of stock in the selected colour DIMS
     but stays clickable — its size pills show sold-out + Notify Me */
  renderAbsorbency() {
    const cur = this.store.data.current;
    if (!cur.absorbencies) return;
    const s = this.store.state;
    const c = s.colour && cur.colours[s.colour];
    this.querySelectorAll('[data-s1-absorbency]').forEach((btn) => {
      /* not-offered levels are Liquid-rendered disabled with the strike +
         "Only available in" hint — the stock dimming must not stack on top */
      if (btn.disabled) return;
      const level = btn.dataset.s1Absorbency;
      btn.setAttribute('aria-pressed', String(level === s.absorbency));
      const lv = c && c.abs && c.abs[level];
      const anyAvail = !!(lv && Object.values(lv.sizes).some((v) => v.available));
      btn.classList.toggle('is-soldout', !anyAvail);
    });
  }

  /* ---- NiteKnit bundle rows: radio row cards + Customize Your Bundle
     drawer, 1:1 port of ProductSection.tsx's Bundle Selector. Savings on
     the cards are the order-discount projection (unitPriceCents), not the
     SPA's fixed pack percentages — the migration's agreed pricing model. */
  /* NiteKnit v1 getHexColor, used when the variant_colours block runs in
     solid-swatch mode: explicit overrides first, then keyword rules */
  swatchHex(name) {
    if (this._swatchCfg === undefined) {
      const wrap = this.querySelector('[data-s1-vcolours]');
      if (wrap && wrap.dataset.s1SwatchStyle === 'hex') {
        const overrides = {};
        (wrap.dataset.s1SwatchHexes || '').split('\n').forEach((line) => {
          const i = line.indexOf(':');
          if (i > 0) overrides[line.slice(0, i).trim().toLowerCase()] = line.slice(i + 1).trim();
        });
        this._swatchCfg = overrides;
      } else {
        this._swatchCfg = null;
      }
    }
    if (!this._swatchCfg) return null;
    const n = String(name).toLowerCase();
    if (this._swatchCfg[n]) return this._swatchCfg[n];
    if (n.includes('black')) return '#000000';
    if (n.includes('grey') || n.includes('gray')) return '#363E43';
    if (n.includes('green')) return '#092C1C';
    if (n.includes('navy') || n.includes('blue')) return '#28324c';
    return '#cccccc';
  }

  renderBundleRows() {
    const root = this.querySelector('[data-s1-bundle-rows]');
    if (!root) return;
    const s = this.store.state;
    const basePrice = this.store.data.current.price;
    const unitNoun = root.dataset.s1Unit || 'Set';
    const colours = this.store.data.current.colours || {};

    const fixedMode = !!this.store.data.fixedPricing;
    root.querySelectorAll('[data-s1-bundle]').forEach((btn) => {
      const count = parseInt(btn.dataset.s1Bundle, 10);
      const active = count === s.tierQty;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
      let pct;
      let unit;
      let totalCents;
      if (fixedMode) {
        /* SPA order of operations: total = price * count * (1 - disc/100),
           per-item = price * (1 - disc/100) — pct comes straight from the
           configured bundle, never re-derived from a rounded unit */
        pct = this.store.fixedPctFor(count) || 0;
        totalCents = Math.round(basePrice * count * (100 - pct) / 100);
        unit = Math.round(basePrice * (100 - pct) / 100);
      } else {
        unit = this.store.unitPriceCents(count);
        pct = Math.round((1 - unit / basePrice) * 100);
        totalCents = unit * count;
      }
      /* XCup renders two decimals always (SPA toFixed(2)); value-tier rows
         keep the trailing-zero-free format the other templates hydrate to */
      const fmt = fixedMode ? moneyFormatCents : moneyFormat;
      const saveEl = btn.querySelector('[data-s1-bundle-save]');
      if (saveEl) {
        saveEl.hidden = pct <= 0;
        saveEl.textContent = `Save ${pct}%`;
      }
      const totalEl = btn.querySelector('[data-s1-bundle-total]');
      if (totalEl) totalEl.textContent = fmt(totalCents);
      const perEl = btn.querySelector('[data-s1-bundle-per]');
      if (perEl) perEl.textContent = fmt(unit);
    });

    root.querySelectorAll('[data-s1-bundle-wrap]').forEach((wrap) => {
      const count = parseInt(wrap.dataset.s1BundleWrap, 10);
      const drawer = wrap.querySelector('[data-s1-bundle-drawer]');
      if (!drawer) return;
      /* the drawer needs the colour machinery; products without a colour
         option keep plain pack behaviour with no per-set customisation */
      const show = this.store.slotMode && count > 1 && count === s.tierQty && this.store.effectiveMode === 'mixed';
      drawer.hidden = !show;
      if (!show) return;

      const slotsEl = drawer.querySelector('[data-s1-bundle-slots]');
      if (!slotsEl) return;
      /* every store emit reaches here (cart refresh, ATC loading toggles) —
         rebuilding the rows then would blow away an open size <select> and
         drop keyboard focus for no visual change. Skip when the slot data
         is identical and only re-focus after a real rebuild. */
      const sig = JSON.stringify([s.slots, s.tierQty]);
      if (slotsEl._s1Sig === sig) return;
      slotsEl._s1Sig = sig;
      const focused = document.activeElement;
      const focusedSlot = focused && slotsEl.contains(focused) && focused.hasAttribute('data-s1-slot-size')
        ? focused.dataset.s1SlotSize
        : null;
      slotsEl.textContent = '';
      s.slots.forEach((slot, idx) => {
        const colour = colours[slot.colour] || {};
        const row = document.createElement('div');
        row.className = 's1-pdp__bslot';

        const thumb = document.createElement('div');
        thumb.className = 's1-pdp__bslot-thumb';
        const thumbSrc = this.colourChunkThumb(slot.colour) || colour.image;
        if (thumbSrc) {
          const img = document.createElement('img');
          img.src = thumbSrc;
          img.alt = slot.colour;
          img.loading = 'lazy';
          thumb.appendChild(img);
        }
        row.appendChild(thumb);

        const info = document.createElement('div');
        info.className = 's1-pdp__bslot-info';
        const label = document.createElement('div');
        label.className = 's1-pdp__bslot-label';
        label.textContent = `${unitNoun} ${idx + 1}`;
        /* SPA: OUT OF STOCK chip when the selected combo has no stock */
        const slotV = slot.size ? this.store.colourVariant(slot.handle, slot.colour, slot.size) : null;
        if (!slotV || !slotV.available) {
          const oos = document.createElement('span');
          oos.className = 's1-pdp__bslot-oos';
          oos.textContent = slot.size ? 'OUT OF STOCK' : 'SELECT SIZE';
          label.appendChild(oos);
        }
        info.appendChild(label);

        const line = document.createElement('div');
        line.className = 's1-pdp__bslot-line';
        const cname = document.createElement('span');
        cname.className = 's1-pdp__bslot-colour';
        cname.textContent = slot.colour;
        line.appendChild(cname);

        const select = document.createElement('select');
        select.className = 's1-pdp__bslot-size' + (slot.size ? '' : ' is-empty');
        select.setAttribute('data-s1-slot-size', String(idx));
        select.setAttribute('aria-label', `${unitNoun} ${idx + 1} size`);
        if (!slot.size) {
          const ph = document.createElement('option');
          ph.value = '';
          ph.textContent = 'Size';
          ph.selected = true;
          ph.disabled = true;
          select.appendChild(ph);
        }
        const sizeMap = (colours[slot.colour] && colours[slot.colour].sizes) || {};
        Object.keys(sizeMap).forEach((size) => {
          const opt = document.createElement('option');
          opt.value = size;
          const available = sizeMap[size] && sizeMap[size].available;
          opt.textContent = available ? size : `${size} (OOS)`;
          opt.disabled = !available;
          opt.selected = size === slot.size;
          select.appendChild(opt);
        });
        line.appendChild(select);
        info.appendChild(line);
        row.appendChild(info);

        const dots = document.createElement('div');
        dots.className = 's1-pdp__bslot-dots';
        Object.keys(colours).forEach((name) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 's1-pdp__bslot-dot' + (name === slot.colour ? ' is-active' : '');
          dot.setAttribute('data-s1-bslot-colour', name);
          dot.setAttribute('data-s1-bslot-index', String(idx));
          dot.title = name;
          dot.setAttribute('aria-label', `${unitNoun} ${idx + 1}: ${name}`);
          const hex = this.swatchHex(name);
          if (hex) {
            dot.style.background = hex;
          } else if (colours[name] && colours[name].image) {
            const di = document.createElement('img');
            di.src = colours[name].image;
            di.alt = '';
            di.loading = 'lazy';
            dot.appendChild(di);
          }
          dots.appendChild(dot);
        });
        row.appendChild(dots);
        slotsEl.appendChild(row);
      });
      if (focusedSlot !== null) {
        const again = slotsEl.querySelector(`[data-s1-slot-size="${focusedSlot}"]`);
        if (again) again.focus();
      }
    });
  }

  renderSlotBuilder() {
    const root = this.querySelector('[data-s1-slot-builder]');
    if (!root || !(this.store.slotMode || this.store.collectionSlots)) return;
    const s = this.store.state;
    const mixed = this.store.effectiveMode === 'mixed';

    /* toggle stays visible at qty 1 (SPA), mixed side shows a lock + hint */
    const lock = root.querySelector('[data-s1-mode-lock]');
    if (lock) lock.hidden = s.tierQty > 1;
    const hint = root.querySelector('[data-s1-builder-hint]');
    if (hint) hint.hidden = s.tierQty > 1;
    const mixedBtn = root.querySelector('[data-s1-mode-mixed-btn]');
    if (mixedBtn) mixedBtn.classList.toggle('is-locked', s.tierQty === 1);

    const panel = root.querySelector('[data-s1-builder-panel]');
    if (panel) panel.hidden = !mixed;
    if (!mixed) return;

    /* progress */
    const count = root.querySelector('[data-s1-builder-count]');
    if (count) count.textContent = `${s.slots.length} / ${s.tierQty}`;
    const fill = root.querySelector('[data-s1-builder-fill]');
    if (fill) fill.style.width = `${Math.min(100, (s.slots.length / s.tierQty) * 100)}%`;

    /* colour strip strikes: struck when the GLOBAL size is unavailable in
       that colour (SPA isAvailableInSize). Collection tiles additionally
       overlay the size letter (womens SPA); tiles stay clickable — adding a
       struck tile opens the slot in the red Select Size state, like the SPA. */
    root.querySelectorAll('[data-s1-builder-add]').forEach((btn) => {
      let out;
      if (this.store.collectionSlots) {
        out = !!(s.size && !this.store.sizeAvailable(btn.dataset.s1Handle, s.size));
        const code = btn.querySelector('[data-s1-builder-code]');
        if (code) {
          code.hidden = !out;
          code.textContent = out ? (SHORT_SIZE[s.size] || s.size) : '';
        }
      } else {
        const v = this.store.colourVariant(btn.dataset.s1Handle, btn.dataset.s1ColourName, s.size);
        out = !(v && v.available);
      }
      btn.classList.toggle('is-soldout', out);
      const strike = btn.querySelector('.s1-pdp__builder-strike');
      /* the strike is an SVG: SVGElement has no `hidden` PROPERTY, so a
         property write never removes the attribute (the slash stayed
         display:none forever) — toggle the attribute itself */
      if (strike) strike.toggleAttribute('hidden', !out);
    });

    /* slots strip (JS-owned DOM) — skip the rebuild when the slot data is
       unchanged (cart refreshes, loading toggles) so an open size <select>
       and keyboard focus survive, and restore focus after real rebuilds */
    const strip = root.querySelector('[data-s1-builder-slots]');
    if (!strip) return;
    const stripSig = JSON.stringify([s.slots, s.tierQty, s.size]);
    if (strip._s1Sig === stripSig) return;
    strip._s1Sig = stripSig;
    const stripFocused = document.activeElement;
    const stripFocusedSlot = stripFocused && strip.contains(stripFocused) && stripFocused.hasAttribute('data-s1-slot-size')
      ? stripFocused.dataset.s1SlotSize
      : null;
    const keepScroll = strip.scrollLeft;
    strip.textContent = '';
    s.slots.forEach((slot, idx) => {
      const p = this.store.products[slot.handle];
      const isCollectionSlot = slot.colour == null;
      const colour = !isCollectionSlot && p && p.colours && p.colours[slot.colour];
      const card = document.createElement('div');
      card.className = 's1-pdp__slot';
      const img = document.createElement('div');
      img.className = 's1-pdp__slot-img' + (slot.size ? '' : ' is-error');
      /* collection slots reuse the SSR strip/grid tile's already-cached 160w
         thumb instead of shipping every image URL in the data island */
      let imgSrc = null;
      if (isCollectionSlot) {
        const tileImg = this.querySelector(`[data-s1-builder-add][data-s1-handle="${CSS.escape(slot.handle)}"] img, [data-s1-colour="${CSS.escape(slot.handle)}"] img`);
        if (tileImg) imgSrc = tileImg.currentSrc || tileImg.src;
      } else {
        imgSrc = colour && colour.image;
      }
      if (imgSrc) {
        const im = document.createElement('img');
        im.src = imgSrc;
        im.alt = isCollectionSlot ? slot.handle : slot.colour;
        im.loading = 'lazy';
        img.appendChild(im);
      }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 's1-pdp__slot-remove';
      remove.setAttribute('data-s1-slot-remove', String(idx));
      remove.setAttribute('aria-label', 'Remove');
      remove.textContent = '×';
      img.appendChild(remove);
      card.appendChild(img);

      if (!isCollectionSlot) {
        /* socks style label; womens slot cards are image + size only (SPA) */
        const label = document.createElement('p');
        label.className = 's1-pdp__slot-type';
        label.textContent = slot.handle.includes('crew') ? 'Crew'
          : slot.handle.includes('ankle') ? 'Ankle'
          : slot.handle.includes('no-show') ? 'No-Show' : '';
        card.appendChild(label);
      }

      const select = document.createElement('select');
      select.className = 's1-pdp__slot-select' + (slot.size ? '' : ' is-error');
      select.setAttribute('data-s1-slot-size', String(idx));
      select.setAttribute('aria-label', 'Size for bundle item ' + (idx + 1));
      const ph = document.createElement('option');
      ph.value = '';
      ph.disabled = true;
      ph.selected = !slot.size;
      ph.textContent = 'Select Size';
      select.appendChild(ph);
      if (isCollectionSlot) {
        /* womens SPA quirk kept 1:1: the option list is the CURRENT product's
           size range; each option disables when THIS slot's product lacks
           that size (label aliases resolved) */
        for (const sizeKey of Object.keys(this.store.data.current.sizes)) {
          const rKey = this.store.resolveSizeKey(p, sizeKey);
          const sv = rKey ? p.sizes[rKey] : null;
          const opt = document.createElement('option');
          opt.value = sizeKey;
          opt.disabled = !(sv && sv.available);
          opt.selected = slot.size === sizeKey;
          opt.textContent = SHORT_SIZE[sizeKey] || sizeKey;
          if (opt.disabled) opt.textContent += ' (OOS)';
          select.appendChild(opt);
        }
      } else if (colour) {
        for (const [sizeKey, sv] of Object.entries(colour.sizes)) {
          const opt = document.createElement('option');
          opt.value = sizeKey;
          opt.disabled = !sv.available;
          opt.selected = slot.size === sizeKey;
          opt.textContent = SHORT_SIZE[sizeKey] || sizeKey;
          if (!sv.available) opt.textContent += ' (OOS)';
          select.appendChild(opt);
        }
      }
      card.appendChild(select);
      strip.appendChild(card);
    });
    for (let i = s.slots.length; i < s.tierQty; i += 1) {
      const empty = document.createElement('div');
      empty.className = 's1-pdp__slot s1-pdp__slot--empty';
      empty.innerHTML = '<span>+</span>';
      strip.appendChild(empty);
    }
    /* SPA scrolls the strip to the freshly added slot; other re-renders
       (slot size changes, cart refreshes) keep the user's scroll position */
    if (this.store.slotAdded) {
      this.store.slotAdded = false;
      strip.scrollTo({ left: Math.max(0, (s.slots.length - 1) * 108), behavior: 'smooth' });
    } else {
      strip.scrollLeft = keepScroll;
    }
    if (stripFocusedSlot !== null) {
      const again = strip.querySelector(`[data-s1-slot-size="${stripFocusedSlot}"]`);
      if (again) again.focus();
    }
  }

  /* ColorVoteModal port: vote -> email (Klaviyo subscribe) -> success, with
     localStorage stepone_sock_voted blocking re-votes */
  initColourVote() {
    const modal = this.querySelector('[data-s1-vote-modal]');
    if (!modal || modal.dataset.s1VoteBound) return;
    modal.dataset.s1VoteBound = 'true';
    this._votePick = null;

    const showStep = (name) => {
      modal.querySelectorAll('[data-s1-vote-step]').forEach((el) => {
        el.hidden = el.dataset.s1VoteStep !== name;
      });
    };
    this._voteShowStep = showStep;

    const form = modal.querySelector('[data-s1-vote-form]');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = modal.querySelector('[data-s1-vote-email]').value.trim();
        const errEl = modal.querySelector('[data-s1-vote-error]');
        const labelEl = modal.querySelector('[data-s1-vote-submit-label]');
        if (!email || !this._votePick || this._voteSubmitting) return;
        this._voteSubmitting = true;
        if (errEl) errEl.hidden = true;
        if (labelEl) labelEl.textContent = 'Submitting...';
        try {
          const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(modal.dataset.klaviyoKey)}`, {
            method: 'POST',
            headers: {
              revision: '2026-04-15',
              'content-type': 'application/json',
              accept: 'application/vnd.api+json',
            },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email,
                        properties: {
                          'Next Sock Color Vote': this._votePick.name,
                          Source: 'Sock Color Voting Modal',
                        },
                        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                      },
                    },
                  },
                },
                relationships: { list: { data: { type: 'list', id: modal.dataset.klaviyoList } } },
              },
            }),
          });
          /* SPA's klaviyo.ts throws on !ok — success only for real 2xx */
          if (!res.ok) throw new Error(`klaviyo subscribe failed: ${res.status}`);
          showStep('success');
          setTimeout(() => this.toggleColourVote(false), 2500);
        } catch (err) {
          console.error('Colour vote subscribe error:', err);
          if (errEl) {
            errEl.textContent = 'Something went wrong. Please try again.';
            errEl.hidden = false;
          }
        } finally {
          this._voteSubmitting = false;
          if (labelEl) labelEl.textContent = 'Submit Vote';
        }
      });
    }
  }

  toggleColourVote(open) {
    const modal = this.querySelector('[data-s1-vote-modal]');
    if (!modal || modal.hidden === !open) return;
    modal.hidden = !open;
    lockBody(open);
    if (open) {
      const voted = localStorage.getItem('stepone_sock_voted') === 'true';
      this._voteShowStep(voted ? 'already-voted' : 'vote');
      this._closeVoteDialog = openDialog(modal, () => this.toggleColourVote(false));
    } else if (this._closeVoteDialog) {
      this._closeVoteDialog();
      this._closeVoteDialog = null;
    }
  }

  /* UGC videos are marked up without autoplay/preload (multi-MB sources
     must not race the LCP image); visibility drives muted playback instead,
     which looks identical to the SPA's autoplay */
  /* bundle grids carry their own modal instances (undies calculator, sock
     chart), created lazily on first open; a recommendation writes back into
     that grid's option dimension when the value exists and is orderable */
  openBundleModal(key) {
    if (!this.bundleModals[key]) {
      const root = this.querySelector(`[data-s1-bundle-modal="${CSS.escape(key)}"]`);
      if (!root) return;
      const idx = parseInt(root.dataset.s1BundleModalOpt, 10);
      this.bundleModals[key] = new S1SizeModal(root, (size) => {
        const opt = this.store.data.bundleOptions && this.store.data.bundleOptions[idx];
        if (!opt) return;
        const names = opt.values.map((val) => val.name);
        const value = names.includes(size)
          ? size
          : names.find((n) => S1PdpStore.SIZE_ALIAS[size] === n) || null;
        if (value && this.store.bundleValueAvailable(idx, value)) {
          this.store.setBundleOption(idx, value);
          this.refreshBundleState();
        }
      });
    }
    this.bundleModals[key].open();
  }

  /* the official high-variant flow: Liquid can't see past 250 variants, so
     every remote-mode selection re-fetches this section with
     ?option_values=<selected value ids> — the server resolves
     product.selected_variant (any position) and re-computes each value's
     availability for the new selection state. Stale responses lose to the
     sequence counter; bundleVariant() additionally rejects any resolution
     whose tuple no longer matches the live selection. */
  async refreshBundleState() {
    if (!this.store.bundleMode || !this.store.data.bundleRemote) return;
    const ids = [];
    this.store.state.bundle.forEach((name, i) => {
      if (name == null) return;
      const v = this.store.data.bundleOptions[i].values.find((x) => x.name === name);
      if (v && v.id) ids.push(v.id);
    });
    if (!ids.length) return;
    const seq = (this._bundleSeq = (this._bundleSeq || 0) + 1);
    try {
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(this.dataset.sectionId)}&option_values=${ids.join(',')}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`bundle option_values fetch ${res.status}`);
      const html = await res.text();
      if (seq !== this._bundleSeq) return;
      const dataEl = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-s1-pdp-data]');
      if (!dataEl) throw new Error('bundle island missing from section response');
      this.store.applyBundleState(JSON.parse(dataEl.textContent));
    } catch (err) {
      /* selection stays valid; the CTA just can't enable without a resolved
         variant — the next click retries naturally */
      console.error('s1-pdp: bundle state refresh failed', err);
    }
  }

  /* Klaviyo behavioural events, replacing the island-page.liquid postMessage
     bridge: "Viewed Product" + trackViewedItem once per product view (a fit
     switch swaps the section in and fires for the new product, like the
     SPA's set_product), "Added to Cart" after every successful add. The
     payload island is Liquid-rendered (s1-pdp-klaviyo snippet) with the
     bridge's field shape so existing Klaviyo flows keep matching. */
  initKlaviyo() {
    const dataEl = this.querySelector('[data-s1-klaviyo]');
    if (!dataEl) return;
    let item;
    try {
      item = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    item.URL = `${window.location.origin}/products/${item.Handle}`;
    delete item.Handle;
    this.klaviyoItem = item;

    window._learnq = window._learnq || [];
    if (window.__s1KlaviyoViewedId !== item.ProductID) {
      window.__s1KlaviyoViewedId = item.ProductID;
      window._learnq.push(['track', 'Viewed Product', item]);
      window._learnq.push(['trackViewedItem', {
        Title: item.Name,
        ItemId: item.ProductID,
        Categories: item.Categories,
        ImageUrl: item.ImageURL,
        Url: item.URL,
        Metadata: {
          Brand: item.Brand,
          Price: item.Price,
          CompareAtPrice: item.CompareAtPrice,
        },
      }]);
    }

    this.store.onAddSuccess = () => {
      window._learnq = window._learnq || [];
      window._learnq.push(['track', 'Added to Cart', this.klaviyoItem]);
    };
  }

  /* XCup AthleteBadge: cycle the stacked pill items — 4s hold, 500ms fade
     out, swap, fade back in (SPA AthleteBadge timings) */
  initAthletes() {
    const wrap = this.querySelector('[data-s1-athletes]');
    if (!wrap) return;
    const items = wrap.querySelectorAll('[data-s1-athlete-item]');
    if (items.length < 2) return;
    if (this._athleteTimer) clearInterval(this._athleteTimer);
    if (this._athleteFade) clearTimeout(this._athleteFade);
    let index = 0;
    this._athleteTimer = setInterval(() => {
      items[index].classList.remove('is-visible');
      this._athleteFade = setTimeout(() => {
        index = (index + 1) % items.length;
        items.forEach((el, i) => el.classList.toggle('is-visible', i === index));
      }, 500);
    }, 4000);
  }

  initUgc() {
    const items = this.querySelectorAll('[data-s1-ugc-item]');
    if (!items.length) return;
    if (this._ugcIo) this._ugcIo.disconnect();
    this._ugcIo = new IntersectionObserver((entries) => {
      for (const en of entries) {
        const v = en.target.querySelector('[data-s1-ugc-video]');
        if (!v) continue;
        if (en.isIntersecting) {
          if (!v.dataset.s1UserPaused && v.paused) v.play().catch(() => {});
        } else if (!v.paused) {
          v.pause();
        }
      }
    }, { rootMargin: '100px' });
    items.forEach((item) => this._ugcIo.observe(item));
  }

  disconnectedCallback() {
    if (this._stickyObserver) this._stickyObserver.disconnect();
    if (this._abort) this._abort.abort();
    if (this._ugcIo) { this._ugcIo.disconnect(); this._ugcIo = null; }
    if (this._athleteTimer) { clearInterval(this._athleteTimer); this._athleteTimer = null; }
    if (this._athleteFade) { clearTimeout(this._athleteFade); this._athleteFade = null; }
    /* balance the ref-counted body lock for every overlay this instance
       leaves open (drawer re-locks on the next connect via migrate) */
    if (this.sizeModal) this.sizeModal.close();
    this.toggleDrawer(false);
    this.toggleFullscreen(false);
    this.closeEngineered();
    this.toggleColourVote(false);
    document.documentElement.removeEventListener('cart:change', this.onCartChange);
    document.removeEventListener('s1:product-change', this.onProductChange);
  }

  bindEvents() {
    this.addEventListener('click', (e) => {
      const fitLink = e.target.closest('[data-s1-fit-link]');
      if (fitLink && !fitLink.classList.contains('is-active')) {
        e.preventDefault();
        return this.switchFit(fitLink);
      }
      if (fitLink) { e.preventDefault(); return; }

      const fitModalBtn = e.target.closest('[data-s1-size-modal-open]');
      if (fitModalBtn && this.sizeModal) return this.sizeModal.open();

      /* SPA NotifyMeButton -> same Klaviyo back-in-stock form as sold-out pills */
      if (e.target.closest('[data-s1-notify-me]')) return this.notifyMe();

      /* variant-colour UI (socks) */
      const vcolourBtn = e.target.closest('[data-s1-vcolour]');
      if (vcolourBtn) return this.pickColour(vcolourBtn.dataset.s1Vcolour);

      /* Socks v2 style & colour tiles: same product -> colour pick in place;
         another fit product -> in-page section swap carrying the tapped
         colour (forceColour beats the migrated one on the other side) */
      const styleTile = e.target.closest('[data-s1-style-tile]');
      if (styleTile) {
        e.preventDefault();
        const name = styleTile.dataset.s1ColourName;
        if (styleTile.dataset.s1Handle === this.store.state.selectedHandle) {
          if (name) this.pickColour(name);
          return;
        }
        /* optimistic pressed state so the tile flips with the click, not
           after the fetch (same reasoning as switchFit) */
        this.querySelectorAll('[data-s1-style-tile]').forEach((t) => t.setAttribute('aria-pressed', String(t === styleTile)));
        const nameEl = this.querySelector('[data-s1-style-name]');
        if (nameEl && styleTile.dataset.s1StyleLabel) nameEl.textContent = styleTile.dataset.s1StyleLabel;
        const migrate = this.store.snapshot();
        if (name) migrate.forceColour = name;
        return this.switchProduct(styleTile.href, migrate, undefined);
      }

      /* Socks v2 "Build Your Own Pack" head link: toggles the slot builder
         (mixed mode) in place of the builder's own Single/Mixed toggle */
      const builderOpen = e.target.closest('[data-s1-builder-open]');
      if (builderOpen) {
        const mixed = this.store.effectiveMode === 'mixed';
        this.store.setMode(mixed ? 'single' : 'mixed');
        if (!mixed && this.store.effectiveMode === 'mixed') {
          const panel = this.querySelector('[data-s1-builder-panel]');
          if (panel && typeof panel.scrollIntoView === 'function') panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        return;
      }
      /* absorbency cards (period underwear) */
      const absBtn = e.target.closest('[data-s1-absorbency]');
      if (absBtn) return this.store.setAbsorbency(absBtn.dataset.s1Absorbency);

      const builderAdd = e.target.closest('[data-s1-builder-add]');
      if (builderAdd) return this.store.addSlot(builderAdd.dataset.s1Handle, builderAdd.dataset.s1ColourName);
      const slotRemove = e.target.closest('[data-s1-slot-remove]');
      if (slotRemove) return this.store.removeSlot(parseInt(slotRemove.dataset.s1SlotRemove, 10));
      const builderTab = e.target.closest('[data-s1-builder-tab]');
      if (builderTab) {
        this.querySelectorAll('[data-s1-builder-tab]').forEach((b) => b.setAttribute('aria-pressed', String(b === builderTab)));
        this.querySelectorAll('[data-s1-builder-strip]').forEach((w) => {
          w.hidden = w.dataset.s1BuilderStrip !== builderTab.dataset.s1BuilderTab;
        });
        return;
      }
      const builderPrev = e.target.closest('[data-s1-builder-prev]');
      if (builderPrev) return this.scrollStep('[data-s1-builder-strip]:not([hidden]) .s1-pdp__builder-strip', -0.75);
      const builderNext = e.target.closest('[data-s1-builder-next]');
      if (builderNext) return this.scrollStep('[data-s1-builder-strip]:not([hidden]) .s1-pdp__builder-strip', 0.75);
      const slotsPrev = e.target.closest('[data-s1-slots-prev]');
      if (slotsPrev) return this.scrollStep('[data-s1-builder-slots]', -0.75);
      const slotsNext = e.target.closest('[data-s1-slots-next]');
      if (slotsNext) return this.scrollStep('[data-s1-builder-slots]', 0.75);

      /* colour vote modal */
      if (e.target.closest('[data-s1-vote-open]')) return this.toggleColourVote(true);
      if (e.target.closest('[data-s1-vote-close]')) return this.toggleColourVote(false);
      const voteSwatch = e.target.closest('[data-s1-vote-colour]');
      if (voteSwatch) {
        this._votePick = { name: voteSwatch.dataset.s1VoteColour, hex: voteSwatch.dataset.s1VoteHex };
        this.querySelectorAll('[data-s1-vote-colour]').forEach((b) => b.classList.toggle('is-selected', b === voteSwatch));
        const cta = this.querySelector('[data-s1-vote-submit]');
        if (cta) cta.disabled = false;
        return;
      }
      const voteSubmit = e.target.closest('[data-s1-vote-submit]');
      if (voteSubmit && this._votePick) {
        const modal = this.querySelector('[data-s1-vote-modal]');
        /* SPA: hex POSTs immediately on Vote Now, voted flag set here too */
        fetch(`${modal.dataset.endpoint}?value=${encodeURIComponent(this._votePick.hex)}`, { method: 'POST', mode: 'no-cors' }).catch(console.error);
        localStorage.setItem('stepone_sock_voted', 'true');
        const picked = modal.querySelector('[data-s1-vote-picked]');
        if (picked) picked.textContent = this._votePick.name;
        this._voteShowStep('email');
        return;
      }
      if (e.target.closest('[data-s1-vote-back]')) {
        this._voteShowStep('vote');
        return;
      }

      /* "Why we engineered this?" fullscreen video, 1:1 with the SPA modal:
         lazy src per breakpoint, click video toggles play/pause */
      if (e.target.closest('[data-s1-engineered-open]')) return this.openEngineered();
      if (e.target.closest('[data-s1-engineered-close]')) return this.closeEngineered();
      const engPlay = e.target.closest('[data-s1-engineered-play]');
      if (engPlay) {
        const v = this.querySelector('[data-s1-engineered-video]');
        if (v) v.play();
        return;
      }
      const engVideo = e.target.closest('[data-s1-engineered-video]');
      if (engVideo) {
        if (engVideo.paused) engVideo.play();
        else engVideo.pause();
        return;
      }

      /* mobile switcher tile: switches the whole product in-page, like the
         SPA's collectionList handleProductChange */
      const switcherTile = e.target.closest('[data-s1-switcher-tile]');
      if (switcherTile) {
        e.preventDefault();
        if (switcherTile.dataset.s1SwitcherHandle !== this.store.state.selectedHandle) {
          return this.switchProduct(switcherTile.dataset.s1SwitcherUrl, this.store.snapshot(), this.dataset.fitCollection || undefined);
        }
        return;
      }
      const switcherPrev = e.target.closest('[data-s1-switcher-prev]');
      if (switcherPrev) return this.scrollSwitcher(-1);
      const switcherNext = e.target.closest('[data-s1-switcher-next]');
      if (switcherNext) return this.scrollSwitcher(1);

      const sizeBtn = e.target.closest('[data-s1-size]');
      if (sizeBtn) {
        /* sold-out sizes open the back-in-stock form instead of selecting,
           mirroring the SPA's handleNotifyMe */
        if (sizeBtn.classList.contains('is-soldout')) return this.notifyMe();
        return this.store.setSize(sizeBtn.dataset.s1Size);
      }

      /* bundle size pills: one grid per option dimension */
      const bundleSizeBtn = e.target.closest('[data-s1-bundle-size]');
      if (bundleSizeBtn) {
        if (bundleSizeBtn.classList.contains('is-soldout')) return;
        this.store.setBundleOption(
          parseInt(bundleSizeBtn.dataset.s1BundleOpt, 10),
          bundleSizeBtn.dataset.s1BundleSize
        );
        return this.refreshBundleState();
      }
      /* each bundle grid's Find My Fit opens that grid's own modal */
      const bundleFit = e.target.closest('[data-s1-bundle-fit]');
      if (bundleFit) return this.openBundleModal(bundleFit.dataset.s1BundleFit);

      /* size / pack strip arrows: scrollSizes = clientWidth / 2,
         scrollPacks = clientWidth * 0.75, per the SPA */
      /* size arrows scroll THEIR OWN row — bundle pages render one carousel
         per option dimension; single-carousel pages resolve identically */
      const sizesPrev = e.target.closest('[data-s1-sizes-prev]');
      if (sizesPrev) return this.scrollSizesFrom(sizesPrev, -0.5);
      const sizesNext = e.target.closest('[data-s1-sizes-next]');
      if (sizesNext) return this.scrollSizesFrom(sizesNext, 0.5);
      const packsPrev = e.target.closest('[data-s1-packs-prev]');
      if (packsPrev) return this.scrollStep('[data-s1-tier-cards]', -0.75);
      const packsNext = e.target.closest('[data-s1-packs-next]');
      if (packsNext) return this.scrollStep('[data-s1-tier-cards]', 0.75);

      const tierBtn = e.target.closest('[data-s1-tier]');
      if (tierBtn) return this.store.setTier(parseInt(tierBtn.dataset.s1Tier, 10));

      /* NiteKnit bundle rows: row click swaps the whole pack (SPA
         handleBundleSelect); per-slot colour dots live in the drawer */
      const bundleBtn = e.target.closest('[data-s1-bundle]');
      if (bundleBtn) {
        const count = parseInt(bundleBtn.dataset.s1Bundle, 10);
        if (count !== this.store.state.tierQty) this.store.setBundleCount(count);
        return;
      }
      const bslotColour = e.target.closest('[data-s1-bslot-colour]');
      if (bslotColour) {
        const idx = parseInt(bslotColour.dataset.s1BslotIndex, 10);
        const colour = bslotColour.dataset.s1BslotColour;
        const slot = this.store.state.slots[idx];
        if (slot && slot.colour !== colour) {
          slot.colour = colour;
          /* keep the size only when the new colour stocks it (theme slot
             convention; SPA keeps it and flags OUT OF STOCK instead) */
          const keep = slot.size || this.store.state.size;
          const v = this.store.colourVariant(slot.handle, colour, keep);
          slot.size = v && v.available ? keep : '';
          this.store.emit('slots');
        }
        return;
      }

      const qtyMinusBtn = e.target.closest('[data-s1-qty-minus]');
      /* with bundle rows the stepper must re-seed the slot pack too, or the
         mixed CTA would sit incomplete forever */
      if (qtyMinusBtn) {
        if (this.querySelector('[data-s1-bundle-rows]')) {
          return this.store.setBundleCount(Math.max(1, this.store.state.tierQty - 1));
        }
        return this.store.bumpTier(-1);
      }
      const qtyPlusBtn = e.target.closest('[data-s1-qty-plus]');
      if (qtyPlusBtn) {
        if (this.querySelector('[data-s1-bundle-rows]')) {
          /* clamp to the largest configured row so the pack never outgrows
             the drawer UI */
          const counts = [...this.querySelectorAll('[data-s1-bundle]')].map((b) => parseInt(b.dataset.s1Bundle, 10));
          const max = counts.length ? Math.max(...counts) : this.store.state.tierQty;
          return this.store.setBundleCount(Math.min(max, this.store.state.tierQty + 1));
        }
        return this.store.bumpTier(1);
      }

      const modeBtn = e.target.closest('[data-s1-mode]');
      if (modeBtn) return this.store.setMode(modeBtn.dataset.s1Mode);

      const viewBtn = e.target.closest('[data-s1-view]');
      if (viewBtn) {
        if (viewBtn.dataset.s1View === 'selected') {
          return this.store.setView(this.store.state.view === 'selected' ? 'all' : 'selected');
        }
        return this.store.setView('all');
      }

      const minus = e.target.closest('[data-s1-colour-minus]');
      if (minus) return this.store.bumpColour(minus.closest('[data-s1-colour]').dataset.s1Colour, -1);

      const plus = e.target.closest('[data-s1-colour-plus]');
      if (plus) return this.store.bumpColour(plus.closest('[data-s1-colour]').dataset.s1Colour, 1);

      const tap = e.target.closest('[data-s1-colour-tap]');
      if (tap) {
        /* tiles are real anchors for crawlability; JS always intercepts */
        e.preventDefault();
        /* inside the grid drawer, tiles only select in single mode (SPA
           GridProductTile: onClick gated on mode === 'single') */
        if (tap.closest('[data-s1-drawer]') && this.store.effectiveMode === 'mixed') return;
        const tile = tap.closest('[data-s1-colour]');
        if (tile.dataset.s1Colour !== this.store.state.selectedHandle) {
          /* scope the fetch to the active fit collection: colour siblings are
             only inferable from fit1-4 membership on the target product, and
             a page-template embed (briefs landing) typically configures just
             its own family — a cross-family session would otherwise snap the
             strip back to fit1. The address bar keeps the plain product URL. */
          return this.switchProduct(tile.dataset.s1ColourUrl, this.store.snapshot(), this.dataset.fitCollection || undefined);
        }
        return;
      }

      const drawerOpen = e.target.closest('[data-s1-drawer-open]');
      if (drawerOpen) return this.toggleDrawer(true);
      const drawerClose = e.target.closest('[data-s1-drawer-close]');
      if (drawerClose) return this.toggleDrawer(false);
      const drawerConfirm = e.target.closest('[data-s1-drawer-confirm]');
      if (drawerConfirm && !drawerConfirm.disabled) return this.toggleDrawer(false);

      const fsClose = e.target.closest('[data-s1-fs-close]');
      if (fsClose) return this.toggleFullscreen(false);
      const fsPrev = e.target.closest('[data-s1-fs-prev]');
      if (fsPrev) return this.fsNav(-1);
      const fsNext = e.target.closest('[data-s1-fs-next]');
      if (fsNext) return this.fsNav(1);

      /* UGC carousel (CloudPressUGCCarousel): mute button toggles sound,
         tapping the tile toggles play/pause with the small play overlay */
      const ugcMute = e.target.closest('[data-s1-ugc-mute]');
      if (ugcMute) {
        const item = ugcMute.closest('[data-s1-ugc-item]');
        const v = item && item.querySelector('[data-s1-ugc-video]');
        if (v) {
          v.muted = !v.muted;
          const mutedIcon = item.querySelector('[data-s1-ugc-muted-icon]');
          const unmutedIcon = item.querySelector('[data-s1-ugc-unmuted-icon]');
          if (mutedIcon) mutedIcon.hidden = !v.muted;
          if (unmutedIcon) unmutedIcon.hidden = v.muted;
        }
        return;
      }
      const ugcItem = e.target.closest('[data-s1-ugc-item]');
      if (ugcItem) {
        const v = ugcItem.querySelector('[data-s1-ugc-video]');
        const overlay = ugcItem.querySelector('[data-s1-ugc-play]');
        if (v) {
          if (v.paused) {
            delete v.dataset.s1UserPaused;
            v.play().catch(() => {});
            if (overlay) overlay.hidden = true;
          } else {
            /* an explicit tap-pause must survive scrolling away and back —
               the visibility observer only auto-resumes untouched videos */
            v.dataset.s1UserPaused = 'true';
            v.pause();
            if (overlay) overlay.hidden = false;
          }
        }
        return;
      }

      /* static gallery video: tap toggles play/pause and unmutes on play,
         1:1 with the SPA's VideoItem (the overlay div would otherwise fall
         through to the fullscreen open below) */
      const staticWrap = e.target.closest('[data-s1-static-video-wrap]');
      if (staticWrap) {
        const v = staticWrap.querySelector('[data-s1-static-video]');
        if (v) {
          if (v.paused) {
            v.play().catch(() => {});
            v.muted = false;
          } else {
            v.pause();
          }
        }
        return;
      }

      /* tap on the gallery itself opens the full-screen viewer, like the
         SPA's cursor-zoom-in container — but not taps on its nav buttons or
         on a video/iframe's native controls */
      const galleryTap = e.target.closest('[data-s1-gallery]');
      if (galleryTap && !e.target.closest('button, video, iframe')) return this.toggleFullscreen(true);

      const colPrev = e.target.closest('[data-s1-colours-prev]');
      if (colPrev) return this.scrollColours(-1);
      const colNext = e.target.closest('[data-s1-colours-next]');
      if (colNext) return this.scrollColours(1);

      const thumb = e.target.closest('[data-s1-thumb]');
      if (thumb) return this.setGalleryIndex(parseInt(thumb.dataset.s1Thumb, 10));

      const scrollReviews = e.target.closest('[data-s1-action="scroll-reviews"]');
      if (scrollReviews) {
        const target = document.getElementById('s1-reviews');
        if (target) target.scrollIntoView({ behavior: 'smooth' });
        return;
      }

      /* "Help me pick" -> the style comparison section (SPA scrollToComparison) */
      const scrollComparison = e.target.closest('[data-s1-action="scroll-comparison"]');
      if (scrollComparison) {
        const target = document.getElementById('style-comparison');
        if (target) target.scrollIntoView({ behavior: 'smooth' });
        return;
      }

      /* generic scroll CTA (e.g. "Explore the Science" -> #fabric, SPA scrollToFabric) */
      const scrollTo = e.target.closest('[data-s1-action="scroll-to"]');
      if (scrollTo) {
        const target = document.getElementById(scrollTo.dataset.s1ScrollTarget);
        if (target) target.scrollIntoView({ behavior: 'smooth' });
        return;
      }

      const accToggle = e.target.closest('[data-s1-accordion-toggle]');
      if (accToggle) {
        const item = accToggle.closest('[data-s1-accordion]');
        const wasOpen = item.classList.contains('is-open');
        this.querySelectorAll('[data-s1-accordion].is-open').forEach((i) => {
          i.classList.remove('is-open');
          i.querySelector('[data-s1-accordion-toggle]')?.setAttribute('aria-expanded', 'false');
        });
        if (!wasOpen) {
          item.classList.add('is-open');
          accToggle.setAttribute('aria-expanded', 'true');
        }
        return;
      }

      const atcBtn = e.target.closest('[data-s1-atc-btn]');
      if (atcBtn && !atcBtn.disabled) {
        if (this.store.ctaState.inCart) return this.openCartDrawer();
        return this.store.addToCart();
      }

      /* sticky bar CTA: adds when the main CTA could, otherwise scrolls the
         shopper back up to the configurator (size / colour still missing) */
      const stickyBtn = e.target.closest('[data-s1-sticky-atc-btn]');
      if (stickyBtn && !stickyBtn.disabled) {
        const cta = this.store.ctaState;
        if (cta.loading) return;
        if (cta.inCart) return this.openCartDrawer();
        if (!cta.disabled) return this.store.addToCart();
        const target = this.querySelector('[data-s1-size-grid], [data-s1-size], [data-s1-atc]') || this;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    });
  }

  /* sticky ATC bar: visible only once the main ATC row has scrolled off
     screen (above or below), so it never doubles the CTA in view */
  initStickyAtc() {
    const bar = this.querySelector('[data-s1-sticky-atc]');
    if (!bar) return;
    /* replace_inline: the bar IS the mobile CTA, so it never hides (the
       inline row is display:none under .s1-pdp--sticky-replace) */
    if (bar.hasAttribute('data-s1-sticky-always')) {
      if (this._stickyObserver) this._stickyObserver.disconnect();
      bar.classList.add('is-visible');
      bar.setAttribute('aria-hidden', 'false');
      return;
    }
    const anchor = this.querySelector('[data-s1-atc]') || this.querySelector('[data-s1-size-grid]');
    if (!anchor || !('IntersectionObserver' in window)) {
      bar.classList.add('is-visible');
      bar.setAttribute('aria-hidden', 'false');
      return;
    }
    if (this._stickyObserver) this._stickyObserver.disconnect();
    /* show_when "scrolled_past": only once the inline CTA is ABOVE the
       viewport; a CTA still below the fold keeps the bar hidden so the page
       doesn't open with a second button on screen */
    const pastOnly = bar.dataset.s1StickyShow === 'scrolled_past';
    /* show_offset: shrink the observed area from the top by N px so the bar
       appears while the inline CTA is still that far from leaving the
       viewport (overlap), instead of only once it is fully gone */
    const offset = Math.max(0, parseInt(bar.dataset.s1StickyOffset, 10) || 0);
    this._stickyObserver = new IntersectionObserver((entries) => {
      const en = entries[entries.length - 1];
      const inView = entries.some((e) => e.isIntersecting);
      let show = !inView;
      if (pastOnly && show && en) show = en.boundingClientRect.bottom < offset;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }, { threshold: 0, rootMargin: `-${offset}px 0px 0px 0px` });
    this._stickyObserver.observe(anchor);
  }

  switchFit(link) {
    const toggle = this.querySelector('[data-s1-fit-toggle]');
    if (toggle) toggle.style.setProperty('--s1-fit-index', link.dataset.s1FitIndex);
    /* optimistic active state, like the SPA's immediate setProductType: the
       label colours must flip WITH the slider, not after the section fetch —
       otherwise the old tab reads white-on-grey and the new one grey-on-black
       for the whole loading window. The fetched section re-renders the same
       classes server-side, so this never drifts. */
    this.querySelectorAll('[data-s1-fit-link]').forEach((a) => {
      const active = a === link;
      a.classList.toggle('is-active', active);
      a.setAttribute('aria-current', String(active));
    });
    /* full snapshot so slot-mode colour/slots survive style switches, like
       the SPA's independent customBundleItems state; restore() drops slots
       whose product/colour no longer resolve */
    const opts = link.hasAttribute('data-s1-fit-full') ? { fullPage: true } : undefined;
    return this.switchProduct(link.href, this.store.snapshot(), undefined, opts);
  }

  async switchProduct(href, migrate, collectionHandle, opts) {
    migrate = migrate || {};
    migrate.firstHandle = this.stripFirstHandle;
    const sectionId = this.dataset.sectionId;
    const url = new URL(href, window.location.origin);
    const seq = (window.__s1PdpSwitchSeq = (window.__s1PdpSwitchSeq || 0) + 1);

    const loading = this.querySelector('[data-s1-gallery-loading]');
    if (loading) loading.hidden = false;

    /* collection-scoped path populates the global `collection` in Liquid so
       the section can honour an explicit colour-grid override (SPA's
       collectionHandle param); the address bar keeps the plain product URL */
    const fetchPath = collectionHandle
      ? `/collections/${encodeURIComponent(collectionHandle)}${url.pathname}`
      : url.pathname;

    try {
      /* fullPage: cross-template targets (bundle packs) — the Section
         Rendering API would render THIS template's settings against the
         target product, so fetch the target's real page and lift its own
         <s1-pdp> element instead */
      const sectionUrl = opts && opts.fullPage
        ? fetchPath
        : `${fetchPath}?section_id=${encodeURIComponent(sectionId)}`;
      let res = await fetch(sectionUrl).catch(() => null);
      if (!res || res.status >= 500) {
        /* transient proxy/bot-check hiccups (dev server 503s) recover on a
           single retry; 4xx (deleted/unpublished product) never will, so
           those fall through to full navigation immediately */
        await new Promise((r) => setTimeout(r, 500));
        res = await fetch(sectionUrl);
      }
      if (!res.ok) throw new Error(`section fetch ${res.status}`);
      const html = await res.text();
      if (seq !== window.__s1PdpSwitchSeq) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('s1-pdp');
      if (!next) throw new Error('section markup missing');

      /* re-capture state at swap time: the old section stayed interactive
         during the fetch, so entry-time values may be stale. Only the keys
         the call site chose to migrate are refreshed (fit switches
         deliberately omit allocation/view). */
      const fresh = this.store.snapshot();
      for (const k of ['size', 'tierQty', 'mode', 'view', 'allocation', 'colour', 'slots', 'bundle']) {
        if (k in migrate) migrate[k] = fresh[k];
      }
      if (this.sockUpsell) migrate.upsell = this.sockUpsell.snapshot();
      const drawerNow = this.querySelector('[data-s1-drawer]');
      migrate.drawerOpen = !!(drawerNow && !drawerNow.hidden);
      const stripNow = this.querySelector('[data-s1-colour-strip]');
      if (stripNow) migrate.stripScroll = stripNow.scrollLeft;

      window.__s1PdpMigrate = migrate;
      const params = new URLSearchParams(window.location.search);
      if (this.dataset.embedded !== undefined) {
        /* embedded on a page template: the shopper stays on the landing page.
           Record the switch as ?product=<handle> on the PAGE URL (the SPA
           iframe's replaceState contract) — no history entry, no popstate
           reload, and a refresh re-boots onto the same colour via the
           deep-link path in connectedCallback */
        const handle = url.pathname.split('/').filter(Boolean).pop();
        params.set('product', handle);
        /* the family must survive a refresh: the boot deep-link can only
           scope its fetch from the URL (SPA parity: ?collection= override).
           Without it, a reload of a cross-family session re-infers from
           fit1-4 and snaps the switcher strip back to the page's own family */
        if (collectionHandle) params.set('collection', collectionHandle);
        else params.delete('collection');
        window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params.toString()}`);
      } else {
        /* keep the current query string (UTMs etc.) on the new product URL */
        const qs = params.toString();
        const newUrl = qs ? `${url.pathname}?${qs}` : url.pathname;
        window.__s1PdpPushed = true;
        window.history.pushState({ s1pdp: true }, '', newUrl);
        window.__s1PdpCurrentPath = url.pathname;
      }
      /* static_gallery_landing_only: the static marketing media belong to the
         product the shopper landed on. Strip them out of the incoming markup so
         the switched-to product opens on its own first image. Thumb indices
         are renumbered because the section offsets them by static_count. */
      if (next.dataset.s1StaticLandingOnly !== undefined) {
        const nTrack = next.querySelector('[data-s1-gallery-track]');
        const staticN = nTrack ? parseInt(nTrack.dataset.s1StaticCount || '0', 10) : 0;
        if (staticN > 0) {
          next.querySelectorAll('[data-s1-static]').forEach((el) => el.remove());
          nTrack.dataset.s1StaticCount = '0';
          next.querySelectorAll('[data-s1-thumb]').forEach((t, i) => {
            t.dataset.s1Thumb = String(i);
            t.setAttribute('aria-label', `Image ${i + 1}`);
            t.classList.toggle('is-active', i === 0);
          });
        }
      }
      this.replaceWith(next);
    } catch (err) {
      /* a newer switch superseded this one while it was failing — the loser
         must not hard-navigate the page back to its own (stale) product */
      if (seq !== window.__s1PdpSwitchSeq) return;
      console.error('s1-pdp switch failed', err);
      /* soft callers (deep-link boot) tolerate failure in place; a shopper's
         own click still deserves the real PDP as a fallback */
      if (opts && opts.soft) return;
      window.location.href = href;
    }
  }

  /* limit-1 "View cart": open the theme's cart drawer, else go to /cart */
  openCartDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.show === 'function') return drawer.show();
    window.location.href = (window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart';
  }

  toggleDrawer(open) {
    const drawer = this.querySelector('[data-s1-drawer]');
    if (!drawer || drawer.hidden === !open) return;
    drawer.hidden = !open;
    lockBody(open);
    if (open) {
      this._closeDrawerDialog = openDialog(drawer, () => this.toggleDrawer(false));
    } else if (this._closeDrawerDialog) {
      this._closeDrawerDialog();
      this._closeDrawerDialog = null;
    }
  }

  toggleFullscreen(open) {
    const fs = this.querySelector('[data-s1-fs]');
    if (!fs || fs.hidden === !open) return;
    fs.hidden = !open;
    lockBody(open);
    if (open) {
      this.syncFullscreen();
      this._closeFsDialog = openDialog(fs, () => this.toggleFullscreen(false));
    } else {
      this.fsResetZoom();
      fs.querySelectorAll('video').forEach((v) => v.pause());
      if (this._closeFsDialog) {
        this._closeFsDialog();
        this._closeFsDialog = null;
      }
    }
  }

  fsNav(dir) {
    if (!this.galleryItems || !this.galleryItems.length) return;
    this.fsResetZoom();
    this.setGalleryIndex(this.galleryIndex + dir);
  }

  /* fullscreen viewer touch: pinch to zoom (1x-4x), one-finger pan while
     zoomed, double-tap toggles 2.5x at the tap point, one-finger swipe at
     1x changes slide. Pointer maths only, no library; the 1600px fs image
     carries 4x on a phone. */
  initFsZoom() {
    const fs = this.querySelector('[data-s1-fs]');
    const viewport = fs && fs.querySelector('.s1-pdp__fs-viewport');
    if (!viewport) return;
    const MIN = 1, MAX = 4, DBL = 2.5;
    const z = { scale: 1, x: 0, y: 0 };
    this._fsZoom = z;
    let start = null;   /* gesture snapshot */
    let lastTap = 0;
    let swipeX = null;

    const media = () => {
      const item = fs.querySelectorAll('.s1-pdp__fs-item')[this.galleryIndex];
      return item ? item.querySelector('img.s1-pdp__fs-media') : null;
    };
    const clamp = () => {
      const r = viewport.getBoundingClientRect();
      const maxX = (r.width * (z.scale - 1)) / 2;
      const maxY = (r.height * (z.scale - 1)) / 2;
      z.x = Math.max(-maxX, Math.min(maxX, z.x));
      z.y = Math.max(-maxY, Math.min(maxY, z.y));
    };
    const apply = (animated) => {
      const img = media();
      if (!img) return;
      img.style.transition = animated ? 'transform 0.25s ease' : 'none';
      img.style.transform = z.scale === 1 ? '' : `translate(${z.x}px, ${z.y}px) scale(${z.scale})`;
      fs.classList.toggle('is-zoomed', z.scale > 1);
    };
    this._fsApplyZoom = apply;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const mid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });
    /* zoom about a viewport point so the pixel under the fingers stays put */
    const zoomAt = (next, px, py) => {
      const r = viewport.getBoundingClientRect();
      const cx = px - r.left - r.width / 2;
      const cy = py - r.top - r.height / 2;
      const k = next / z.scale;
      z.x = cx - (cx - z.x) * k;
      z.y = cy - (cy - z.y) * k;
      z.scale = next;
      clamp();
    };

    viewport.addEventListener('touchstart', (e) => {
      const t = e.touches;
      if (t.length === 2) {
        start = { d: dist(t), scale: z.scale, m: mid(t), x: z.x, y: z.y };
        swipeX = null;
      } else if (t.length === 1) {
        start = { one: true, px: t[0].clientX, py: t[0].clientY, x: z.x, y: z.y };
        swipeX = z.scale === 1 ? t[0].clientX : null;
      }
    }, { passive: true });

    viewport.addEventListener('touchmove', (e) => {
      const t = e.touches;
      if (!start) return;
      if (t.length === 2 && start.d) {
        e.preventDefault();
        const m = mid(t);
        const next = Math.max(MIN, Math.min(MAX, start.scale * (dist(t) / start.d)));
        zoomAt(next, start.m.x, start.m.y);
        z.x += m.x - start.m.x; z.y += m.y - start.m.y;
        start.m = m; start.scale = next; start.d = dist(t);
        clamp(); apply(false);
      } else if (t.length === 1 && start.one && z.scale > 1) {
        e.preventDefault();
        z.x = start.x + (t[0].clientX - start.px);
        z.y = start.y + (t[0].clientY - start.py);
        clamp(); apply(false);
      }
    }, { passive: false });

    viewport.addEventListener('touchend', (e) => {
      if (e.touches.length) { start = null; return; }
      const ch = e.changedTouches[0];
      /* pinched below 1x: snap back */
      if (z.scale <= MIN + 0.05) { z.scale = 1; z.x = 0; z.y = 0; apply(true); }
      if (start && start.one && ch) {
        const moved = Math.hypot(ch.clientX - start.px, ch.clientY - start.py);
        const now = Date.now();
        if (moved < 12) {
          if (now - lastTap < 300) {
            /* double tap: toggle 2.5x at the tap point */
            if (z.scale > 1) { z.scale = 1; z.x = 0; z.y = 0; } else zoomAt(DBL, ch.clientX, ch.clientY);
            apply(true);
            lastTap = 0;
          } else lastTap = now;
        } else if (swipeX !== null && z.scale === 1) {
          const dx = ch.clientX - swipeX;
          if (dx < -50) this.fsNav(1);
          else if (dx > 50) this.fsNav(-1);
        }
      }
      start = null; swipeX = null;
    });
  }

  fsResetZoom() {
    const z = this._fsZoom;
    if (!z || (z.scale === 1 && !z.x && !z.y)) return;
    const img = this.querySelector('[data-s1-fs] .s1-pdp__fs-media[style]');
    z.scale = 1; z.x = 0; z.y = 0;
    if (img) { img.style.transition = 'none'; img.style.transform = ''; }
    const fs = this.querySelector('[data-s1-fs]');
    if (fs) fs.classList.remove('is-zoomed');
  }

  /* "Why we engineered this?" video modal: mobile/desktop src picked on
     open, paused state dims the video and shows the big play button */
  openEngineered() {
    const modal = this.querySelector('[data-s1-engineered-modal]');
    const video = this.querySelector('[data-s1-engineered-video]');
    if (!modal || !video) return;
    if (!video.src) {
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      const src = (mobile ? video.dataset.srcMobile : video.dataset.srcDesktop) || video.dataset.srcDesktop;
      /* no configured URL: assigning undefined would serialize to a relative
         "undefined" request — keep the modal closed instead */
      if (!src) return;
      video.src = src;
      const overlay = this.querySelector('[data-s1-engineered-play]');
      video.addEventListener('pause', () => {
        video.classList.add('is-paused');
        if (overlay) overlay.hidden = false;
      });
      video.addEventListener('play', () => {
        video.classList.remove('is-paused');
        if (overlay) overlay.hidden = true;
      });
    }
    modal.hidden = false;
    lockBody(true);
    video.play().catch(() => {});
    this._closeEngDialog = openDialog(modal, () => this.closeEngineered());
  }

  closeEngineered() {
    const modal = this.querySelector('[data-s1-engineered-modal]');
    if (!modal || modal.hidden) return;
    const video = this.querySelector('[data-s1-engineered-video]');
    if (video) video.pause();
    modal.hidden = true;
    lockBody(false);
    if (this._closeEngDialog) {
      this._closeEngDialog();
      this._closeEngDialog = null;
    }
  }

  /* iOS Safari: a non-first gallery video ships with preload="none" and no
     autoplay attribute, and is play()ed here when its slide becomes active.
     Safari leaves such an element on its poster with a play button unless it
     has started loading and is muted as a live property (the attribute alone
     is not enough after a pause()/re-play), while Android Chrome just plays.
     Prime it once: force muted/inline, switch preload on and load(), then
     play(). Subsequent activations skip the load() so the clip is not reset. */
  primeAndPlay(v) {
    v.muted = true;
    v.defaultMuted = true;
    if (!v.hasAttribute('playsinline')) v.setAttribute('playsinline', '');
    if (!v.hasAttribute('webkit-playsinline')) v.setAttribute('webkit-playsinline', '');
    if (v.preload === 'none' && !v.dataset.s1Primed) {
      v.dataset.s1Primed = '1';
      v.preload = 'auto';
      /* load() aborts a play() issued in the same tick, so the first play
         waits for the element to be ready; later activations play directly */
      v.addEventListener('canplay', () => { if (v.dataset.s1WantPlay) v.play().catch(() => {}); }, { once: true });
      v.dataset.s1WantPlay = '1';
      v.load();
      return;
    }
    v.play().catch(() => {});
  }

  /* mirror the main gallery index into the full-screen track/counter and
     play only the active video (autoplay-on-slide, like the SPA modal) */
  syncFullscreen() {
    const fs = this.querySelector('[data-s1-fs]');
    if (!fs || fs.hidden) return;
    const track = fs.querySelector('[data-s1-fs-track]');
    if (track) track.style.transform = `translateX(-${this.galleryIndex * 100}%)`;
    const range = this.galleryRange();
    const current = fs.querySelector('[data-s1-fs-current]');
    if (current) current.textContent = String(this.galleryIndex - range.start + 1);
    const total = fs.querySelector('[data-s1-fs-total]');
    if (total) total.textContent = String(range.end - range.start);
    fs.querySelectorAll('.s1-pdp__fs-item').forEach((item, idx) => {
      const v = item.querySelector('video');
      if (!v) return;
      if (idx === this.galleryIndex) this.primeAndPlay(v);
      else v.pause();
    });
  }

  /* per-tile image carousels in the grid drawer: sync the active dot */
  initGridDrawer() {
    this.querySelectorAll('[data-s1-gd-carousel]').forEach((car) => {
      car.addEventListener('scroll', () => {
        const idx = Math.round(car.scrollLeft / car.clientWidth);
        const dots = car.parentElement.querySelectorAll('.s1-pdp__gd-dot');
        dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      }, { passive: true });
    });
  }

  /* Klaviyo back-in-stock form, region-mapped like the SPA's useNotifyMe
     (AU SAr9cz / UK SigtsQ / US Ww9h6r); the SPA posts to its parent frame,
     the theme talks to Klaviyo onsite directly */
  notifyMe() {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD';
    const formId = currency === 'GBP' ? 'SigtsQ' : currency === 'USD' ? 'Ww9h6r' : 'SAr9cz';
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
  }

  scrollStep(stripSelector, factor) {
    const strip = this.querySelector(stripSelector);
    if (strip) strip.scrollBy({ left: strip.clientWidth * factor, behavior: 'smooth' });
  }

  scrollSizesFrom(btn, factor) {
    const carousel = btn.closest('.s1-pdp__size-carousel');
    const strip = (carousel && carousel.querySelector('[data-s1-size-scroll]'))
      || this.querySelector('[data-s1-size-scroll]');
    if (strip) strip.scrollBy({ left: strip.clientWidth * factor, behavior: 'smooth' });
  }

  /* checkSizeScroll / checkPackScroll port: below 1024px arrows are forced
     visible; on desktop they follow the actual scroll position (pack arrows
     are additionally display:none on desktop via CSS, like lg:hidden) */
  initStepCarousels() {
    const wire = (strip, prevSel, nextSel) => {
      if (!strip) return;
      const prev = this.querySelector(prevSel);
      const next = this.querySelector(nextSel);
      if (!prev || !next) return;
      const check = () => {
        if (window.innerWidth < 1024) {
          prev.hidden = false;
          next.hidden = false;
        } else {
          prev.hidden = !(strip.scrollLeft > 0);
          next.hidden = !(strip.scrollLeft < strip.scrollWidth - strip.clientWidth - 1);
        }
      };
      strip.addEventListener('scroll', check, { passive: true });
      /* fit switching replaces this element; the abort signal keeps window
         listeners from piling up across swaps. Checks are kept so a
         re-connect can rewire them. */
      this._resizeChecks.push(check);
      window.addEventListener('resize', check, { signal: this._abort.signal });
      check();
      setTimeout(check, 100);
    };
    wire(this.querySelector('.s1-pdp__step:not(.s1-pdp__step--bundle-sizes) [data-s1-size-scroll]'), '[data-s1-sizes-prev]', '[data-s1-sizes-next]');
    wire(this.querySelector('[data-s1-tier-cards]'), '[data-s1-packs-prev]', '[data-s1-packs-next]');

    /* bundle pages: one carousel per option dimension, each arrow pair
       follows its own row's scroll state */
    this.querySelectorAll('.s1-pdp__step--bundle-sizes .s1-pdp__size-carousel').forEach((carousel) => {
      const strip = carousel.querySelector('[data-s1-size-scroll]');
      const prev = carousel.querySelector('[data-s1-sizes-prev]');
      const next = carousel.querySelector('[data-s1-sizes-next]');
      if (!strip || !prev || !next) return;
      const check = () => {
        if (window.innerWidth < 1024) {
          prev.hidden = false;
          next.hidden = false;
        } else {
          prev.hidden = !(strip.scrollLeft > 0);
          next.hidden = !(strip.scrollLeft < strip.scrollWidth - strip.clientWidth - 1);
        }
      };
      strip.addEventListener('scroll', check, { passive: true });
      this._resizeChecks.push(check);
      window.addEventListener('resize', check, { signal: this._abort.signal });
      check();
      setTimeout(check, 100);
    });
  }

  scrollSwitcher(dir) {
    const strip = this.querySelector('[data-s1-switcher-strip]');
    if (strip) strip.scrollBy({ left: dir * strip.clientWidth * 0.75, behavior: 'smooth' });
  }

  /* An in-page product switch replaces the whole section, so the freshly
     rendered strip starts at scrollLeft 0 — a tile picked from deep in the
     strip lands off-screen and the shopper has to swipe back to find their
     selection. Centre the selected tile instead (instantly, before paint
     settles); rAF because the strip is display-driven by lg:hidden and has
     no layout in the same frame the element connects on some browsers. */
  initSwitcherPosition() {
    const strip = this.querySelector('[data-s1-switcher-strip]');
    const selected = strip && strip.querySelector('.is-selected');
    if (!strip || !selected) return;
    const centre = () => {
      if (!strip.clientWidth) return; /* strip hidden (desktop) */
      const target = selected.offsetLeft - (strip.clientWidth - selected.offsetWidth) / 2;
      if (target > 0) strip.scrollLeft = target;
    };
    centre();
    requestAnimationFrame(centre);
  }

  scrollColours(dir) {
    const strip = this.querySelector('[data-s1-colour-strip]');
    if (strip) strip.scrollBy({ left: dir * strip.clientWidth * 0.8, behavior: 'smooth' });
  }

  /* gallery: translateX slide, matching the SPA's duration-500 ease-out */

  initGallery() {
    this.galleryIndex = 0;
    const track = this.querySelector('[data-s1-gallery-track]');
    if (!track) return;
    this.galleryItems = track.querySelectorAll('[data-s1-gallery-item]');
    const count = this.galleryItems.length;

    /* static prepended media (CloudPress amazonImages): variant mediaIndex
       jumps must skip past these, and static videos start paused with the
       SPA's play overlay instead of autoplaying on slide */
    this.galleryStaticCount = parseInt(track.dataset.s1StaticCount || '0', 10);

    /* NiteKnit colour groups (getMediaForColor): media are fixed chunks of
       N per colour in colour order; navigation stays inside the selected
       colour's chunk, so only its images are ever reachable */
    this.galleryGroupSize = parseInt(track.dataset.s1GroupSize || '0', 10);
    track.querySelectorAll('[data-s1-static-video]').forEach((v) => {
      const wrap = v.closest('[data-s1-static-video-wrap]');
      const overlay = wrap && wrap.querySelector('[data-s1-static-play]');
      v.addEventListener('play', () => { if (overlay) overlay.hidden = true; });
      v.addEventListener('pause', () => { if (overlay) overlay.hidden = false; });
    });

    /* raw i±1: setGalleryIndex wraps within the active colour chunk (or the
       whole gallery when grouping is off) */
    this.querySelector('[data-s1-gallery-prev]')?.addEventListener('click', () =>
      this.setGalleryIndex(this.galleryIndex - 1));
    this.querySelector('[data-s1-gallery-next]')?.addEventListener('click', () =>
      this.setGalleryIndex(this.galleryIndex + 1));

    /* thumbnail strip arrows: one thumb per click (clientWidth / 4), same as
       the SPA's scrollThumbnails */
    const thumbStrip = this.querySelector('[data-s1-thumbs]');
    if (thumbStrip) {
      this.querySelector('[data-s1-thumbs-prev]')?.addEventListener('click', () =>
        thumbStrip.scrollBy({ left: -thumbStrip.clientWidth / 4, behavior: 'smooth' }));
      this.querySelector('[data-s1-thumbs-next]')?.addEventListener('click', () =>
        thumbStrip.scrollBy({ left: thumbStrip.clientWidth / 4, behavior: 'smooth' }));
    }

    const gallery = this.querySelector('[data-s1-gallery]');
    let touchStartX = null;
    let touchEndX = null;
    gallery.addEventListener('touchstart', (e) => {
      touchEndX = null;
      touchStartX = e.targetTouches[0].clientX;
    }, { passive: true });
    gallery.addEventListener('touchmove', (e) => {
      touchEndX = e.targetTouches[0].clientX;
    }, { passive: true });
    gallery.addEventListener('touchend', () => {
      if (touchStartX === null || touchEndX === null) return;
      const distance = touchStartX - touchEndX;
      if (distance > 50) this.setGalleryIndex(this.galleryIndex + 1);
      else if (distance < -50) this.setGalleryIndex(this.galleryIndex - 1);
    });
  }

  /* a colour's media chunk [start, end). Boundaries prefer VARIANT-IMAGE
     ANCHORS — each colour's featured media marks its chunk start, so
     products sharing this template can have different counts per colour.
     Anchors are used only when every colour has one and they ascend in
     colour order; otherwise fixed chunks of galleryGroupSize apply (the
     v1 getMediaForColor convention). Returns null when ungrouped. */
  colourRange(name) {
    const total = this.galleryItems ? this.galleryItems.length : 0;
    const per = this.galleryGroupSize || 0;
    if (!per || !this.store) return null;
    const colours = this.store.data.current.colours || {};
    const keys = Object.keys(colours);
    const idx = keys.indexOf(name);
    if (idx < 0) return null;
    const offset = this.galleryStaticCount || 0;

    if (this._galleryAnchors === undefined) {
      const anchors = keys.map((k) => {
        const m = colours[k] && colours[k].mediaIndex;
        return typeof m === 'number' && m >= 0 ? offset + m : -1;
      });
      let valid = anchors.length > 0 && anchors[0] >= 0;
      for (let i = 1; i < anchors.length && valid; i += 1) {
        if (anchors[i] < 0 || anchors[i] <= anchors[i - 1]) valid = false;
      }
      if (valid) {
        /* snap the first chunk back to the start of product media so a
           lead image before the first variant image isn't orphaned */
        anchors[0] = offset;
        /* a video sitting directly before a colour's variant image is that
           colour's hero clip (video, then the anchor image) — start the
           chunk on the video, otherwise it lands in the previous colour */
        for (let i = 1; i < anchors.length; i += 1) {
          const before = this.galleryItems[anchors[i] - 1];
          if (anchors[i] - 1 > anchors[i - 1] && before && before.querySelector('video')) anchors[i] -= 1;
        }
        this._galleryAnchors = anchors;
      } else {
        this._galleryAnchors = null;
      }
    }

    if (this._galleryAnchors) {
      const start = this._galleryAnchors[idx];
      const end = idx + 1 < this._galleryAnchors.length ? this._galleryAnchors[idx + 1] : total;
      if (start < total && end > start) return { start, end: Math.min(end, total) };
      return null;
    }

    const start = offset + idx * per;
    if (start >= total) return null;
    return { start, end: Math.min(start + per, total) };
  }

  /* current colour's chunk; spans everything when grouping is off */
  galleryRange() {
    const total = this.galleryItems ? this.galleryItems.length : 0;
    const range = this.store && this.store.state.colour
      ? this.colourRange(this.store.state.colour)
      : null;
    return range || { start: 0, end: total };
  }

  /* colour change: land on the chunk's first image and hide the other
     colours' thumbnails. The SPA swaps the filtered array outright, so the
     jump must NOT animate through the colours in between — the track's
     transition is suspended for this one move. */
  syncGalleryGroup() {
    if (!this.galleryGroupSize) return;
    const range = this.galleryRange();
    this.querySelectorAll('[data-s1-thumb]').forEach((t) => {
      const idx = parseInt(t.dataset.s1Thumb, 10);
      t.hidden = idx < range.start || idx >= range.end;
    });
    if (this.galleryIndex < range.start || this.galleryIndex >= range.end) {
      const tracks = this.querySelectorAll('[data-s1-gallery-track], [data-s1-fs-track]');
      tracks.forEach((t) => { t.style.transition = 'none'; });
      this.setGalleryIndex(range.start);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          tracks.forEach((t) => { t.style.transition = ''; });
        });
      });
    } else {
      this.setGalleryIndex(this.galleryIndex);
    }
  }

  /* v1 drawer thumbnails: the colour chunk's 8th image (mediaItems[7] ||
     mediaItems[0]), read from the thumbnail strip's preview images;
     ungrouped galleries keep the variant image */
  colourChunkThumb(name) {
    const range = this.colourRange(name);
    if (!range) return null;
    const eighth = range.start + 7;
    const pick = (eighth < range.end ? this.querySelector(`[data-s1-thumb="${eighth}"] img`) : null)
      || this.querySelector(`[data-s1-thumb="${range.start}"] img`);
    return pick ? pick.currentSrc || pick.src : null;
  }

  setGalleryIndex(i) {
    /* templates without a gallery track never ran initGallery — a throw here
       would abort connectedCallback and leave the whole PDP uninitialized */
    if (!this.galleryItems) return;
    const range = this.galleryRange();
    if (i < range.start) i = range.end - 1;
    if (i >= range.end) i = range.start;
    this.galleryIndex = i;
    const track = this.querySelector('[data-s1-gallery-track]');
    if (track) track.style.transform = `translateX(-${i * 100}%)`;

    const counter = this.querySelector('[data-s1-gallery-counter]');
    if (counter) counter.textContent = `${i - range.start + 1} / ${range.end - range.start}`;

    /* page dots (gallery_indicator "dots"): one per media item; dots outside
       the active colour chunk hide so the row always reads as the chunk */
    this.querySelectorAll('[data-s1-gallery-dot]').forEach((dot, idx) => {
      dot.hidden = idx < range.start || idx >= range.end;
      dot.classList.toggle('is-active', idx === i);
    });

    this.querySelectorAll('[data-s1-thumb]').forEach((t) => {
      const active = parseInt(t.dataset.s1Thumb, 10) === i;
      t.classList.toggle('is-active', active);
      /* centre the active thumb inside ITS strip only. scrollIntoView also
         scrolls the page to reach the strip, which yanked landing pages
         (buy box below the fold) down on first render. */
      if (active) {
        const strip = t.parentElement;
        if (!strip) return;
        const left = t.offsetLeft - (strip.clientWidth - t.offsetWidth) / 2;
        const top = t.offsetTop - (strip.clientHeight - t.offsetHeight) / 2;
        strip.scrollTo({ left: Math.max(0, left), top: Math.max(0, top), behavior: 'smooth' });
      }
    });

    this.galleryItems.forEach((item, idx) => {
      const v = item.querySelector('video');
      if (!v) return;
      /* static videos stay paused until tapped (SPA VideoItem); product
         videos autoplay when their slide becomes active */
      if (idx === i) {
        if (!v.hasAttribute('data-s1-static-video')) this.primeAndPlay(v);
      } else {
        delete v.dataset.s1WantPlay;
        v.pause();
      }
    });

    this.syncFabricBadge();
    this.syncFullscreen();
  }

  /* v1 fabric badge: visible only on the colour chunk's first slide, image
     swapped per colour from the Colour Name:URL map */
  syncFabricBadge() {
    const badge = this.querySelector('[data-s1-fabric-badge]');
    if (!badge) return;
    const range = this.galleryRange();
    badge.hidden = this.galleryIndex !== range.start;
    const img = badge.querySelector('[data-s1-fabric-badge-img]');
    if (!img) return;
    if (this._fabricImages === undefined) {
      const map = {};
      (badge.dataset.s1FabricImages || '').split('\n').forEach((line) => {
        const i = line.indexOf(':');
        if (i > 0) map[line.slice(0, i).trim().toLowerCase()] = line.slice(i + 1).trim();
      });
      this._fabricImages = map;
    }
    const colour = ((this.store && this.store.state.colour) || '').toLowerCase();
    const src = this._fabricImages[colour];
    if (src) {
      if (img.getAttribute('src') !== src) img.src = src;
      img.hidden = false;
    } else {
      img.hidden = true;
    }
  }

  render() {
    const s = this.store.state;
    const mode = this.store.effectiveMode;
    const total = this.store.totalSelected;
    /* per-selected-size when the island carries it (womens), else product-level */
    const basePrice = this.store.currentPricing().price;

    this.querySelectorAll('[data-s1-size]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.s1Size === s.size));
    });

    /* bundle grids: pressed state per option dimension; availability follows
       the store (static marginal data locally, refreshed per selection by
       the option_values flow in remote mode) */
    if (this.store.bundleMode) {
      this.querySelectorAll('[data-s1-bundle-size]').forEach((btn) => {
        const idx = parseInt(btn.dataset.s1BundleOpt, 10);
        const name = btn.dataset.s1BundleSize;
        btn.setAttribute('aria-pressed', String(s.bundle[idx] === name));
        const out = !this.store.bundleValueAvailable(idx, name);
        btn.classList.toggle('is-soldout', out);
        btn.disabled = out;
        const strike = btn.querySelector('.s1-pdp__size-strike');
        if (strike) strike.toggleAttribute('hidden', !out);
      });
    }

    /* variant-colour families: size availability follows the SELECTED
       colour (the SPA/Liquid first paint only checks the lead colour, which
       strikes sizes other colours still stock — and vice versa). The store
       already resolves CTA and cart items per colour; this keeps the pills
       and the Notify Me tile in sync with it. */
    if (this.store.slotMode && s.colour) {
      let anyOos = false;
      this.querySelectorAll('[data-s1-size]').forEach((btn) => {
        const v = this.store.colourVariant(s.selectedHandle, s.colour, btn.dataset.s1Size);
        const out = !(v && v.available);
        if (out) anyOos = true;
        btn.classList.toggle('is-soldout', out);
        btn.disabled = out;
      });
      /* NiteKnit's tile is static (product-level, data-s1-notify-static);
         other layouts keep following the selected colour */
      const notifyTile = this.querySelector('[data-s1-notify-me]');
      if (notifyTile && notifyTile.dataset.s1NotifyStatic === undefined) {
        notifyTile.hidden = !anyOos;
      }
    }

    const activeTier = this.store.activeTierCount();
    const cartPct = this.store.valueTierPct(this.store.cartSubtotal);
    /* static pack pricing: the unlock pill compares against an empty cart,
       like the price beside it; the "Applied" badge below still reports
       the real cart tier */
    const pillCartPct = this.store.data.staticTierPricing ? 0 : cartPct;
    this.querySelectorAll('[data-s1-tier]').forEach((btn) => {
      const count = parseInt(btn.dataset.s1Tier, 10);
      btn.setAttribute('aria-pressed', String(count === activeTier));
      const unit = this.store.unitPriceCents(count);
      const pct = Math.round((1 - unit / basePrice) * 100);
      const priceEl = btn.querySelector('[data-s1-tier-price]');
      if (priceEl) {
        /* womens SPA renders tier units with toFixed(2) */
        priceEl.textContent = this.classList.contains('s1-pdp--wm') ? moneyFormatCents(unit) : moneyFormat(unit);
        /* SPA colours the whole price (incl. /each) green whenever the unit
           is discounted — independent of the unlock pill's visibility */
        priceEl.classList.toggle('is-discounted', pct > 0);
      }
      const offEl = btn.querySelector('[data-s1-tier-off]');
      if (offEl) {
        /* variants may template the pill text (e.g. "ADD FOR {pct}% OFF");
           templated pills are unlock CTAs (SPA exp-C isUnlock) and hide
           unless the tier beats the cart's current one — the default plain
           "{pct}% OFF" pill is price info and stays for any discount */
        const fmt = offEl.dataset.s1OffFormat;
        offEl.hidden = fmt ? pct <= pillCartPct : pct <= 0;
        offEl.textContent = fmt ? fmt.replaceAll('{pct}', pct) : `${pct}% OFF`;
      }
      if (this.store.packsDisabled && count > 1) btn.setAttribute('disabled', '');
    });

    /* "{pct}% OFF Your Order Applied" pill from the tier the cart is already
       in (CloudMesh variant), same source as the sock upsell badge */
    const appliedEl = this.querySelector('[data-s1-tier-applied]');
    if (appliedEl) {
      appliedEl.hidden = cartPct <= 0;
      const appliedText = appliedEl.querySelector('[data-s1-tier-applied-text]');
      if (appliedText) appliedText.textContent = `${cartPct}% OFF Your Order Applied`;
    }

    const modeToggle = this.querySelector('[data-s1-mode-toggle]');
    if (modeToggle) {
      /* slot/collection builders keep the toggle visible at qty 1 (locked, SPA) */
      modeToggle.hidden = s.tierQty === 1 && !this.store.slotMode && !this.store.collectionSlots;
      modeToggle.classList.toggle('is-mixed', mode === 'mixed');
    }

    this.renderVariantColours();
    this.renderStyleTiles();
    this.renderTierReadout();
    this.renderAbsorbency();
    this.renderSlotBuilder();
    this.renderBundleRows();
    this.querySelectorAll('[data-s1-mode]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.s1Mode === mode));
    });

    const filterRow = this.querySelector('[data-s1-filter-row]');
    if (filterRow) filterRow.hidden = !(s.tierQty > 1 && mode === 'mixed');
    this.querySelectorAll('[data-s1-view]').forEach((btn) => {
      const active = btn.dataset.s1View === 'selected' && s.view === 'selected';
      btn.setAttribute('aria-pressed', String(active));
      if (btn.dataset.s1View === 'selected') {
        btn.disabled = total === 0;
        const dot = btn.querySelector('[data-s1-filter-dot]');
        if (dot) dot.hidden = total === 0;
      }
    });

    this.querySelectorAll('[data-s1-colour]').forEach((tile) => {
      const handle = tile.dataset.s1Colour;
      const qty = s.allocation[handle] || 0;
      const sizeBlocked = s.size && !this.store.sizeAvailable(handle, s.size);
      tile.classList.toggle('is-selected', handle === s.selectedHandle);
      if (tile.closest('[data-s1-scolours]')) {
        /* sibling swatch row (CloudPress look): a colour missing the chosen
           size DIMS instead of vanishing — hiding would shrink the row right
           after hydration and make the colour undiscoverable */
        tile.hidden = false;
        tile.classList.toggle('is-soldout', !!sizeBlocked);
        return;
      }
      if (tile.closest('[data-s1-selector-only]')) {
        /* womens grid: size-unavailable tiles DIM with a slash + the size
           letter and stay clickable (SPA WomensProductSection tiles) —
           no steppers, no Selected filter on this strip */
        tile.hidden = false;
        tile.classList.toggle('is-soldout', !!sizeBlocked);
        const strike = tile.querySelector('[data-s1-colour-strike]');
        /* SVG: toggle the hidden ATTRIBUTE — the property doesn't exist on
           SVGElement and a property write leaves the attribute in place */
        if (strike) strike.toggleAttribute('hidden', !sizeBlocked);
        const codeEl = tile.querySelector('[data-s1-colour-code]');
        if (codeEl) {
          codeEl.hidden = !sizeBlocked;
          codeEl.textContent = sizeBlocked ? (SHORT_SIZE[s.size] || s.size) : '';
        }
        return;
      }
      tile.hidden = !!sizeBlocked || (s.view === 'selected' && qty === 0);
      const count = tile.querySelector('[data-s1-colour-count]');
      if (count) { count.hidden = !(mode === 'mixed' && qty > 0); count.textContent = qty; }
      const steppers = tile.querySelector('[data-s1-colour-steppers]');
      if (steppers) {
        steppers.hidden = !(mode === 'mixed' && s.tierQty > 1);
        const stepCount = steppers.querySelector('[data-s1-stepper-count]');
        if (stepCount) {
          stepCount.textContent = qty;
          stepCount.classList.toggle('is-zero', qty === 0);
        }
        const minusBtn = steppers.querySelector('[data-s1-colour-minus]');
        if (minusBtn) minusBtn.disabled = qty === 0;
        const plusBtn = steppers.querySelector('[data-s1-colour-plus]');
        if (plusBtn) plusBtn.disabled = total >= s.tierQty || !!sizeBlocked;
      }
    });

    /* both progress bars (colour grid + drawer footer) share the markup */
    this.querySelectorAll('[data-s1-progress]').forEach((progress) => {
      progress.hidden = !(s.tierQty > 1 && mode === 'mixed');
      const label = progress.querySelector('[data-s1-progress-label]');
      const countEl = progress.querySelector('[data-s1-progress-count]');
      const fill = progress.querySelector('[data-s1-progress-fill]');
      const complete = total === s.tierQty;
      if (label) label.textContent = complete ? 'Pack Complete!' : 'Select your items';
      if (countEl) countEl.textContent = `${total} / ${s.tierQty}`;
      if (fill) fill.style.width = `${Math.min(100, (total / s.tierQty) * 100)}%`;
      progress.classList.toggle('is-complete', complete);
    });

    /* drawer confirm mirrors the SPA footer: mixed pack counts down, single
       mode always confirms */
    const drawerConfirm = this.querySelector('[data-s1-drawer-confirm]');
    if (drawerConfirm) {
      if (mode === 'mixed' && s.tierQty > 1) {
        const complete = total === s.tierQty;
        drawerConfirm.disabled = !complete;
        drawerConfirm.textContent = complete ? 'Confirm Selection' : `Select ${s.tierQty - total} more`;
      } else {
        drawerConfirm.disabled = false;
        drawerConfirm.textContent = 'Confirm Selection';
      }
    }

    const qtyCount = this.querySelector('[data-s1-qty-count]');
    if (qtyCount) qtyCount.textContent = s.tierQty;
    const qtyMinus = this.querySelector('[data-s1-qty-minus]');
    if (qtyMinus) qtyMinus.disabled = s.tierQty === 1;
    const qtyPlus = this.querySelector('[data-s1-qty-plus]');
    /* womens SPA has no packsDisabled guard on the "+" stepper — limited
       editions can buy multiple units (no tier cards, still single mode);
       the mens/kids layouts keep the pin (per Felix). Bundles ride
       packsDisabled only to open at 1 pack — their stepper is free too. */
    if (qtyPlus) qtyPlus.disabled = this.store.limitOne || (this.store.packsDisabled && !this.store.collectionSlots && !this.store.bundleMode && !this.store.freeStepper);

    const cta = this.store.ctaState;
    const atcBtn = this.querySelector('[data-s1-atc-btn]');
    /* limit-1: the stepper can never step, so it goes; the note sits under
       the CTA until the unit is in the cart, then the CTA itself says so */
    const qtyWrap = this.querySelector('[data-s1-qty]');
    if (qtyWrap && this.store.limitOne) qtyWrap.hidden = true;
    const atcNote = this.querySelector('[data-s1-atc-note]');
    if (atcNote) atcNote.hidden = !this.store.limitOne;
    if (atcBtn) {
      atcBtn.classList.toggle('s1-pdp__atc-btn--incart', !!cta.inCart);
      atcBtn.disabled = cta.disabled;
      const labelEl = atcBtn.querySelector('[data-s1-atc-label]');
      if (labelEl) {
        if (this.atcDefaultLabel === undefined) this.atcDefaultLabel = labelEl.textContent;
        let label = cta.label;
        /* v1: a multi-pack with an unavailable set reads "Selection Out of
           Stock" (bundle-rows pages only; other mixed layouts keep theirs) */
        if (!label && cta.disabled && !cta.loading
            && this.querySelector('[data-s1-bundle-rows]')
            && this.store.effectiveMode === 'mixed') {
          label = 'Selection Out of Stock';
        }
        labelEl.textContent = label || this.atcDefaultLabel;
      }
      const spinnerEl = atcBtn.querySelector('[data-s1-atc-spinner]');
      if (spinnerEl) spinnerEl.hidden = !cta.loading;
    }
    const atcError = this.querySelector('[data-s1-atc-error]');
    if (atcError) {
      atcError.hidden = !s.cartError;
      atcError.textContent = s.cartError || '';
    }

    /* sticky bar mirrors the CTA; a disabled main CTA becomes an enabled
       "Select options" button that scrolls back up (click handler) */
    const stickyBtn = this.querySelector('[data-s1-sticky-atc-btn]');
    if (stickyBtn) {
      const stickyLabel = stickyBtn.querySelector('[data-s1-sticky-btn-label]');
      const stickySpinner = stickyBtn.querySelector('[data-s1-sticky-spinner]');
      const bar = stickyBtn.closest('[data-s1-sticky-atc]');
      const ownLabel = stickyBtn.dataset.s1StickyLabel || (atcBtn && this.atcDefaultLabel) || 'Add to Cart';
      stickyBtn.classList.toggle('is-select', cta.disabled && !cta.loading);
      stickyBtn.classList.toggle('is-incart', !!cta.inCart);
      stickyBtn.disabled = cta.loading;
      if (stickyLabel) {
        if (cta.inCart) stickyLabel.textContent = cta.label;
        else if (cta.loading) stickyLabel.textContent = cta.label || ownLabel;
        else if (cta.disabled) stickyLabel.textContent = (bar && bar.dataset.s1StickySelectLabel) || 'Select options';
        else stickyLabel.textContent = ownLabel;
      }
      if (stickySpinner) stickySpinner.hidden = !cta.loading;
    }
    const stickyPrice = this.querySelector('[data-s1-sticky-price]');
    if (stickyPrice) {
      const fmt = this.store.data.fixedPricing ? moneyFormatCents : moneyFormat;
      stickyPrice.textContent = fmt(this.store.unitPriceCents(s.tierQty));
    }
    /* summary layout: "L · Mixed" from whatever is pressed right now —
       bundle grids and the size pills first, then the active fit card/pill */
    const stickySummary = this.querySelector('[data-s1-sticky-summary]');
    if (stickySummary) {
      const parts = [];
      const txt = (el) => (el && el.textContent ? el.textContent.replace(/\s+/g, ' ').trim() : '');
      /* colour (active fit card / pill) first, then the pressed size(s) */
      const fit = this.querySelector('[data-s1-fit-link].is-active');
      if (fit) {
        const t = txt(fit.querySelector('.s1-pdp__pack-card-label, .s1-pdp__fit-label') || fit);
        if (t) parts.push(t);
      }
      this.querySelectorAll('[data-s1-bundle-size][aria-pressed="true"], [data-s1-size][aria-pressed="true"]').forEach((btn) => {
        const t = txt(btn.querySelector('.s1-pdp__size-text') || btn);
        if (t) parts.push(t);
      });
      const bar = stickySummary.closest('[data-s1-sticky-atc]');
      stickySummary.textContent = parts.length ? parts.join(' \u00b7 ') : ((bar && bar.dataset.s1StickyEmpty) || 'Select your size');
      stickySummary.classList.toggle('is-empty', parts.length === 0);
    }

    
    /* express buy (dynamic checkout): the wallet form can only express a
       single variant line, so sync id/qty when the configured pack collapses
       to one variant and hide it for mixed packs or upsell-included adds */
    const buyBtn = this.querySelector('[data-s1-buy-button]');
    if (buyBtn && cta.inCart) {
      /* limit-1 unit already in the cart: express checkout would add another */
      buyBtn.hidden = true;
    } else if (buyBtn) {
      const items = cta.disabled ? [] : this.store.buildItems();
      const single = items.length === 1 ? items[0] : null;
      /* bundles always collapse to one variant, so the button can sit
         visible (inert until both sizes are picked, like the ATC) instead
         of popping in after selection */
      if (this.store.bundleMode) {
        buyBtn.hidden = false;
        buyBtn.classList.toggle('is-disabled', !single);
      } else {
        buyBtn.hidden = !single;
      }
      if (single) {
        const idInput = buyBtn.querySelector('[data-s1-buy-button-id]');
        const qtyInput = buyBtn.querySelector('[data-s1-buy-button-qty]');
        if (idInput) idInput.value = single.id;
        if (qtyInput) qtyInput.value = single.quantity;
      }
    }

    /* header price, matching titleSection: strike + red when a quantity
       discount or compare-at applies, %OFF pill only for compare-at */
    const unitNow = this.store.unitPriceCents(s.tierQty);
    const compareAt = this.store.currentPricing().compareAt;
    const hasCompareAt = compareAt > basePrice;
    const hasQtyDiscount = unitNow < basePrice;
    this.querySelectorAll('[data-s1-price-row]').forEach((row) => {
      const priceEl = row.querySelector('[data-s1-price]');
      const strikeEl = row.querySelector('[data-s1-price-strike]');
      const offPill = row.querySelector('[data-s1-price-off]');
      if (!priceEl || !strikeEl || !offPill) return;
      /* NiteKnit header pins the base price: pack/order savings only ever
         speak through the bundle rows, never the price row. A merchandised
         compare-at sale price is NOT a pack saving and still strikes
         through, mirroring the SSR branch and the SPA's pinned layout. */
      if (row.dataset.s1PriceStatic !== undefined) {
        /* fixed-pricing templates (XCup) render two decimals like the SPA's
           raw Storefront amount; the %OFF pill never shows there (SPA has a
           plain strike-through only) */
        const fixedMode = !!this.store.data.fixedPricing;
        const fmt = fixedMode ? moneyFormatCents : moneyFormat;
        if (hasCompareAt) {
          const pct = Math.round(((compareAt - basePrice) / compareAt) * 100);
          strikeEl.hidden = false;
          strikeEl.textContent = fmt(compareAt);
          offPill.hidden = fixedMode || pct <= 0;
          offPill.textContent = `${pct}% OFF`;
          priceEl.classList.add('is-discounted');
        } else {
          strikeEl.hidden = true;
          offPill.hidden = true;
          priceEl.classList.remove('is-discounted');
        }
        priceEl.textContent = fmt(basePrice);
        return;
      }
      if (hasQtyDiscount || hasCompareAt) {
        strikeEl.hidden = false;
        strikeEl.textContent = moneyFormat(hasCompareAt ? compareAt : basePrice);
        priceEl.textContent = moneyFormat(hasQtyDiscount ? unitNow : basePrice);
        priceEl.classList.add('is-discounted');
        if (hasCompareAt) {
          const pct = Math.round(((compareAt - basePrice) / compareAt) * 100);
          offPill.hidden = pct <= 0;
          offPill.textContent = `${pct}% OFF`;
        } else {
          offPill.hidden = true;
        }
      } else {
        strikeEl.hidden = true;
        offPill.hidden = true;
        priceEl.textContent = moneyFormat(basePrice);
        priceEl.classList.remove('is-discounted');
      }
    });

    /* v1 SAVE badge: the selected pack's total saving under the price */
    const savings = (basePrice - unitNow) * s.tierQty;
    this.querySelectorAll('[data-s1-price-save]').forEach((el) => {
      el.hidden = savings <= 0;
      if (savings > 0) el.textContent = `SAVE ${moneyFormat(savings)}`;
    });

    /* upsell tier pricing depends on cartSubtotal, so re-render on any emit */
    if (this.sockUpsell) this.sockUpsell.render();
  }
}

class S1ProductNav extends HTMLElement {
  connectedCallback() {
    if (this._initialized) {
      /* click listeners live on the element and survive; only the window
         resize listener needs rewiring after a disconnect */
      if (this._navCheck) {
        this._navAbort = new AbortController();
        window.addEventListener('resize', this._navCheck, { signal: this._navAbort.signal });
        this._navCheck();
      }
      return;
    }
    this._initialized = true;

    this.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-s1-nav-toggle]');
      if (toggle) {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        this.querySelector('[data-s1-nav]')?.classList.toggle('is-expanded', !expanded);
        return;
      }

      const prev = e.target.closest('[data-s1-nav-prev]');
      if (prev) return this.scrollStrip(-1);
      const next = e.target.closest('[data-s1-nav-next]');
      if (next) return this.scrollStrip(1);

      const card = e.target.closest('[data-s1-nav-inpage]');
      if (card) {
        /* SPA nav items dispatch handle + collectionHandle together — the
           collection rides along so the swapped section infers the right
           colour grid (womens 7-way nav exceeds the fit1-4 membership chain) */
        const detail = { url: card.href };
        if (card.dataset.collection) detail.collection = card.dataset.collection;
        const consumed = !document.dispatchEvent(new CustomEvent('s1:product-change', {
          cancelable: true,
          detail,
        }));
        if (consumed) e.preventDefault();
      }
    });

    const strip = this.querySelector('[data-s1-nav-strip]');
    if (!strip) return;
    const prevBtn = this.querySelector('[data-s1-nav-prev]');
    const nextBtn = this.querySelector('[data-s1-nav-next]');
    const check = () => {
      const canLeft = strip.scrollLeft > 0;
      const canRight = strip.scrollLeft < strip.scrollWidth - strip.clientWidth - 5;
      if (prevBtn) prevBtn.hidden = !canLeft;
      if (nextBtn) nextBtn.hidden = !canRight;
      strip.classList.toggle('is-centered', !canLeft && !canRight);
    };
    strip.addEventListener('scroll', check, { passive: true });
    this._navCheck = check;
    this._navAbort = new AbortController();
    window.addEventListener('resize', check, { signal: this._navAbort.signal });
    check();
    setTimeout(check, 1000);
  }

  disconnectedCallback() {
    if (this._navAbort) this._navAbort.abort();
  }

  scrollStrip(dir) {
    const strip = this.querySelector('[data-s1-nav-strip]');
    if (strip) strip.scrollBy({ left: dir * 400, behavior: 'smooth' });
  }
}

if (!customElements.get('s1-pdp')) customElements.define('s1-pdp', S1Pdp);
if (!customElements.get('s1-product-nav')) customElements.define('s1-product-nav', S1ProductNav);
